/**
 * ReporteCaptacion.jsx
 * ─────────────────────────────────────────────────────────────────────
 * Reporte generativo de captación bancaria R2421.
 * Modos: sectorial (sistema completo) | institucional (un banco).
 *
 * Dependencias externas (CDN en index.html):
 *   - Leaflet 1.9.x  →  <link rel="stylesheet" href="https://unpkg.com/leaflet/dist/leaflet.css"/>
 *                        <script src="https://unpkg.com/leaflet/dist/leaflet.js"></script>
 *   - Chart.js 4.x   →  <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
 */

import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { C as _C } from "./theme";
// Los componentes visuales compartidos están disponibles en ReporteComponents.jsx
// para uso en nuevos reportes. ReporteCaptacion mantiene sus propias definiciones
// por compatibilidad con el código existente en producción.

// ── Constantes ───────────────────────────────────────────────────────
const API_URL     = process.env.REACT_APP_BACKEND_URL || "https://cnbv-agente-7bdezditva-uc.a.run.app";
const PERIODOS    = ["202607","202606","202605","202604","202603","202602","202601","202512","202511","202510","202509"];
const INSTITUCIONES = [
  "Banco Base",
  "Intercam Banco",
  "CIBanco",
  "Monex",
  "Sabadell",
  "Banco del Bajío",
  "Banregio",
  "Multiva",
  "Ve por Más",
  "Banca Mifel",
  "Bansí",
  "Mizuho Bank",
  "Bankaool",
  "Dondé Banco",
  "KEB Hana México",
  "Bancrea",
  "Inmobiliario Mexicano",
  "Ualá",
  "Bank of China",
  "Actinver",
  "Shinhan",
  "Kapital",
  "Covalto",
  "ICBC",
  "Revolut Bank",
  "Banco Bineo",
  "Compartamos",
  "Bancoppel",
  "Volkswagen Bank",
  "Openbank",
  "Consubanco",
  "Banco Plata",
  "Banco Azteca",
  "Banfeliz",
  "Hey Banco",
  "BBVA México",
  "Santander",
  "Banamex",
  "Inbursa",
  "Scotiabank",
  "Banorte",
  "HSBC",
  "Credit Suisse",
  "Afirme",
  "Banco S3",
  "MUFG Bank",
  "Invex",
  "BIAfirme",
  "Citi México",
  "Bank of America",
  "J.P. Morgan",
  "Pagatodo",
];
const GEOJSON_URL =
  "https://raw.githubusercontent.com/angelnmara/geojson/master/mexicoHigh.json";
const GEOJSON_MUN_URL = "/municipios_mx.geojson";

// ── Singleton cache de GeoJSONs ───────────────────────────────────────
// Evita que cada mount del componente descargue y parsee el archivo de nuevo.
// La primera llamada inicia el fetch; las siguientes reutilizan el resultado.
let _cacheEstados   = null;
let _promEstados    = null;
let _cacheMunicipios = null;
let _promMunicipios  = null;

function fetchGeoEstados() {
  if (_cacheEstados)  return Promise.resolve(_cacheEstados);
  if (_promEstados)   return _promEstados;
  _promEstados = fetch(GEOJSON_URL).then(r => r.json())
    .then(d => { _cacheEstados = d; return d; });
  return _promEstados;
}

function fetchGeoMunicipios() {
  if (_cacheMunicipios) return Promise.resolve(_cacheMunicipios);
  if (_promMunicipios)  return _promMunicipios;
  _promMunicipios = fetch(GEOJSON_MUN_URL).then(r => r.json())
    .then(d => { _cacheMunicipios = d; return d; });
  return _promMunicipios;
}
const METRIC_LABELS = {
  saldo_total_b:    "Saldo total (mmdp)",
  saldo_vista_b:    "Saldo vista (mmdp)",
  saldo_plazo_b:    "Saldo plazo (mmdp)",
  num_cuentas:      "Número de cuentas",
  saldo_promedio_mxn: "Saldo promedio (MXN)",
};

// ── Paleta — usa tokens de theme.js ──────────────────────────────────
const COLOR = {
  ..._C,
  // Alias legacy para compatibilidad interna
  primary:   _C.surface,
  lightBlue: _C.border,
  gray:      _C.muted,
  grayLight: _C.card,
};

// ── Helpers ──────────────────────────────────────────────────────────
const fmt = (n, dec = 1) => (n ?? 0).toFixed(dec);
const fmtB = n => `${fmt(n, 2)} mmdp`;
const fmtPct = n => n == null ? "—" : `${n > 0 ? "+" : ""}${fmt(n, 1)}%`;
const fmtN = n => (n ?? 0).toLocaleString("es-MX");

// ══════════════════════════════════════════════════════════════════════
// COMPONENTES REUTILIZABLES
// ══════════════════════════════════════════════════════════════════════

/** Tarjeta KPI */
function KpiCard({ label, value, unit, delta, deltaLabel }) {
  const pos = delta > 0;
  const neg = delta < 0;
  return (
    <div style={{
      background: COLOR.card, borderRadius: 6, padding: "12px 16px",
      border: `1px solid ${COLOR.border}`, flex: 1, minWidth: 160,
    }}>
      <div style={{ fontSize: 13, color: COLOR.muted, fontWeight: 700, fontFamily: "monospace",
                    textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: COLOR.gold, lineHeight: 1, fontFamily: "monospace" }}>
        {value} <span style={{ fontSize: 15, fontWeight: 400, color: COLOR.muted }}>{unit}</span>
      </div>
      {delta != null && (
        <div style={{ fontSize: 14, marginTop: 5, fontFamily: "monospace",
                      color: pos ? COLOR.green : neg ? COLOR.red : COLOR.muted }}>
          {pos ? "▲" : neg ? "▼" : "─"} {fmtPct(delta)}
          {deltaLabel && <span style={{ color: COLOR.dim }}> {deltaLabel}</span>}
        </div>
      )}
    </div>
  );
}

/** Barra horizontal */
function BarRow({ label, value, maxValue, color, rightLabel }) {
  const pct = maxValue > 0 ? Math.max(2, (value / maxValue) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 120, fontSize: 14, color: COLOR.muted, fontFamily: "monospace",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 6, background: COLOR.border, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%",
                      background: color || COLOR.gold, borderRadius: 4 }} />
      </div>
      <div style={{ fontSize: 14, color: COLOR.muted, width: 70, fontFamily: "monospace",
                    textAlign: "right", flexShrink: 0 }}>
        {rightLabel}
      </div>
    </div>
  );
}

/** Encabezado de sección */
function SectionHeader({ icon, title, subtitle, badge, badgeColor }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 16px", borderBottom: `1px solid ${COLOR.border}`,
    }}>
      <span style={{ fontSize: 16, color: COLOR.gold }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 16, color: COLOR.text, fontFamily: "monospace",
                      letterSpacing: "0.03em" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: COLOR.muted, marginTop: 1 }}>{subtitle}</div>}
      </div>
      {badge && (
        <span style={{
          fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 700,
          fontFamily: "monospace", letterSpacing: "0.04em",
          background: badgeColor?.bg || `${COLOR.gold}22`,
          color: badgeColor?.text || COLOR.gold,
          border: `1px solid ${COLOR.goldDim}`,
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

/** Limpia markdown residual que el LLM puede colar */
function stripMarkdown(raw) {
  return (raw || "")
    .replace(/^#{1,3}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^---+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Caja de insight LLM */
function InsightBox({ text }) {
  if (!text) return null;
  const clean = stripMarkdown(text);
  // Usar indexOf para localizar "Prioridades de revisión" — sin riesgo de backtracking
  const PRIO_MARKER = "prioridades de revisi";
  const lc = clean.toLowerCase();
  const prioIdx = lc.indexOf(PRIO_MARKER);
  const narrativa   = prioIdx >= 0 ? clean.slice(0, prioIdx).trim() : clean;
  const prioridades = prioIdx >= 0 ? clean.slice(prioIdx).trim() : null;
  const parrafos    = narrativa.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const prioItems   = prioridades
    ? prioridades
        .replace(/^[^:]+:\s*/i, "")
        .split(/\n\d+\.\s+/)
        .map(p => p.trim())
        .filter(Boolean)
    : [];
  return (
    <div style={{
      background: `${COLOR.gold}0d`, border: `1px solid ${COLOR.goldDim}`,
      borderRadius: 6, padding: "12px 16px", fontSize: 15, lineHeight: 1.75,
      color: COLOR.text, marginTop: 10,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.05em",
        background: `${COLOR.gold}22`, color: COLOR.gold,
        display: "inline-block", padding: "1px 8px", borderRadius: 4, marginBottom: 10,
        border: `1px solid ${COLOR.goldDim}`,
      }}>
        ✦ Comentario analítico
      </div>
      {parrafos.map((p, i) => (
        <p key={i} style={{ margin: "0 0 10px 0" }}>{p}</p>
      ))}
      {prioItems.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${COLOR.goldDim}44`, paddingTop: 10 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.05em",
            color: COLOR.gold, marginBottom: 8, textTransform: "uppercase",
          }}>
            Prioridades de revisi\u00f3n
          </div>
          {prioItems.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
              <span style={{
                flexShrink: 0, width: 18, height: 18, borderRadius: "50%",
                background: `${COLOR.gold}22`, border: `1px solid ${COLOR.goldDim}`,
                fontSize: 12, fontWeight: 700, color: COLOR.gold,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "monospace", marginTop: 2,
              }}>{i + 1}</span>
              <span style={{ fontSize: 14, lineHeight: 1.6, color: COLOR.muted }}>{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAPA COROPLÉTICO — LEAFLET
// ══════════════════════════════════════════════════════════════════════

// Normaliza texto para comparación (quita acentos, mayúsculas, puntuación)
function normalizeStr(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z ]/g, "")
    .trim()
    .replace(/\s+/g, " ");
}

// Mapa: código ISO "MX-VER" → cve_estado INEGI (1-32)
// Permite lookup por código aunque el GeoJSON use strings como "MX-VER"
const MX_ISO_TO_CVE = {
  "MX-AGU":1, "MX-BCN":2, "MX-BCS":3, "MX-CAM":4, "MX-COA":5,
  "MX-COL":6, "MX-CHP":7, "MX-CHH":8, "MX-CMX":9, "MX-DUR":10,
  "MX-GUA":11,"MX-GRO":12,"MX-HID":13,"MX-JAL":14,"MX-MEX":15,
  "MX-MIC":16,"MX-MOR":17,"MX-NAY":18,"MX-NLE":19,"MX-OAX":20,
  "MX-PUE":21,"MX-QUE":22,"MX-ROO":23,"MX-SLP":24,"MX-SIN":25,
  "MX-SON":26,"MX-TAB":27,"MX-TAM":28,"MX-TLA":29,"MX-VER":30,
  "MX-YUC":31,"MX-ZAC":32,
};

// Equivalencias de nombre bidireccionales (BigQuery puede devolver nombre largo o corto)
const ALIAS_ESTADO = {
  "michoacan de ocampo":             "michoacan",
  "coahuila de zaragoza":            "coahuila",
  "veracruz de ignacio de la llave": "veracruz",
  "ciudad de mexico":                "distrito federal",
  // Inverso: nombre corto (GeoJSON) → nombre largo (BigQuery oficial INEGI)
  "michoacan":       "michoacan de ocampo",
  "coahuila":        "coahuila de zaragoza",
  "veracruz":        "veracruz de ignacio de la llave",
  "distrito federal":"ciudad de mexico",
};

function MapaCaptacion({ territorioData, metrica }) {
  const mapRef     = useRef(null);
  const leafletRef = useRef(null);
  const geoRef     = useRef(null);
  const [geojson, setGeojson] = useState(null);

  // Cargar GeoJSON una vez
  useEffect(() => {
    fetchGeoEstados()
      .then(setGeojson)
      .catch(e => console.warn("GeoJSON no disponible:", e));
  }, []);

  // Índice dual: por cve_estado (int) Y por nombre normalizado (+ alias bidireccional)
  const dataIdx = React.useMemo(() => {
    if (!territorioData) return { byCode: {}, byName: {} };
    const byCode = Object.fromEntries(territorioData.map(d => [d.cve_estado, d]));
    const byName = {};
    territorioData.forEach(d => {
      const key = normalizeStr(d.nombre_estado);
      byName[key] = d;
      // Indexar también el nombre alternativo (largo ↔ corto) para match robusto
      const alt = ALIAS_ESTADO[key];
      if (alt) byName[alt] = d;
    });
    return { byCode, byName };
  }, [territorioData]);

  // Escala cuantil: ordena los valores y usa la posición en el ranking como pct
  // Evita que un outlier (CDMX) haga que todo lo demás quede en el primer bucket
  const sortedVals = React.useMemo(() => {
    if (!territorioData?.length) return [];
    return territorioData
      .map(d => d[metrica] || 0)
      .filter(v => v > 0)
      .sort((a, b) => a - b);
  }, [territorioData, metrica]);

  const colorScale = useCallback((val) => {
    if (!val || val <= 0 || !sortedVals.length) return "#1a2535";
    // Búsqueda binaria del cuantil
    let lo = 0, hi = sortedVals.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedVals[mid] < val) lo = mid + 1; else hi = mid;
    }
    const pct = sortedVals.length > 1 ? lo / (sortedVals.length - 1) : 1;
    if (pct < 0.08) return "#1e3a8a";
    if (pct < 0.20) return "#0369a1";
    if (pct < 0.34) return "#0891b2";
    if (pct < 0.50) return "#0d9488";
    if (pct < 0.65) return "#16a34a";
    if (pct < 0.80) return "#ca8a04";
    if (pct < 0.92) return "#ea580c";
    return "#dc2626";
  }, [sortedVals]);

  // Lookup de feature → data
  const lookupFeature = useCallback((feature) => {
    // 1. Intenta por código numérico (cve_estado 1-32)
    let cve = parseInt(feature.properties?.CVE_ENT || feature.properties?.cve_ent || 0);
    // El GeoJSON de estados usa id tipo "MX-VER" — convertir a número vía tabla
    if (!(cve > 0)) {
      const mxId = feature.properties?.id || feature.properties?.ID || "";
      cve = MX_ISO_TO_CVE[mxId] || 0;
    }
    if (cve > 0 && dataIdx.byCode[cve]) return dataIdx.byCode[cve];

    // 2. Fallback por nombre (incluye alias largo ↔ corto en byName)
    const rawName = normalizeStr(
      feature.properties?.ESTADO ||
      feature.properties?.estado ||
      feature.properties?.name ||
      feature.properties?.NAME || ""
    );
    return dataIdx.byName[rawName] || dataIdx.byName[ALIAS_ESTADO[rawName]] || null;
  }, [dataIdx]);

  // Inicializar mapa — SIN tile layer (solo GeoJSON sobre fondo oscuro)
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    if (leafletRef.current) {
      leafletRef.current.invalidateSize();
      return;
    }

    leafletRef.current = window.L.map(mapRef.current, {
      center: [23.8, -102.3],
      zoom: 5,
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: true,
      // Restricción de bounds para que no se vea más allá de México
      maxBounds: [[13.5, -120.0], [33.5, -85.0]],
      maxBoundsViscosity: 0.8,
    });

    // Sin tile layer — fondo oscuro puro, solo polígonos de México
    // Doble invalidateSize: inmediato (layout) + diferido (post-render completo)
    setTimeout(() => leafletRef.current?.invalidateSize(), 50);
    setTimeout(() => leafletRef.current?.invalidateSize(), 300);
  }, []);

  // Actualizar capa GeoJSON cuando cambian datos o métrica
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletRef.current || !geojson || !territorioData) return;

    if (geoRef.current) {
      geoRef.current.remove();
      geoRef.current = null;
    }

    geoRef.current = L.geoJSON(geojson, {
      style: (feature) => {
        const d = lookupFeature(feature);
        const val = d?.[metrica] || 0;
        return {
          fillColor:   colorScale(val),
          fillOpacity: 0.92,
          color:       "#070c12",
          weight:      0.8,
        };
      },
      onEachFeature: (feature, layer) => {
        const d = lookupFeature(feature);
        const nombre = d?.nombre_estado ||
          feature.properties?.ESTADO ||
          feature.properties?.estado || "—";
        const val = d?.[metrica];
        const valStr = val == null ? "Sin datos"
          : metrica.includes("_b") ? fmtB(val)
          : metrica === "num_cuentas" ? fmtN(val)
          : `$${fmtN(val)}`;
        layer.bindTooltip(`
          <div style="font-family:monospace;padding:6px 8px;min-width:170px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
            <div style="font-size:12px;font-weight:700;color:#dde4ee;margin-bottom:5px">${nombre}</div>
            <div style="font-size:10px;color:#c8a96e;font-weight:700;margin-bottom:4px">
              ${METRIC_LABELS[metrica] || metrica}: ${valStr}
            </div>
            <div style="font-size:10px;color:#8aa4b8;line-height:1.6">
              Total: ${fmtB(d?.saldo_total_b)}<br/>
              Vista: ${fmtB(d?.saldo_vista_b)} · Plazo: ${fmtB(d?.saldo_plazo_b)}<br/>
              Cuentas: ${fmtN(d?.num_cuentas)}<br/>
              Prom: $${fmtN(d?.saldo_promedio_mxn)}
            </div>
          </div>
        `, { sticky: true, opacity: 1, className: "leaflet-tooltip-emrisk" });
        layer.on("mouseover", () => layer.setStyle({ weight: 2, color: "#c8a96e", fillOpacity: 1 }));
        layer.on("mouseout",  () => geoRef.current?.resetStyle(layer));
      },
    }).addTo(leafletRef.current);

    // Ajustar zoom para mostrar México completo
    try {
      leafletRef.current.fitBounds(geoRef.current.getBounds(), {
        padding: [12, 12],
        maxZoom: 7,
      });
    } catch (_) {}

    leafletRef.current.invalidateSize();
    setTimeout(() => leafletRef.current?.invalidateSize(), 150);
  }, [geojson, dataIdx, metrica, colorScale, lookupFeature, territorioData]);

  return (
    <div ref={mapRef}
      style={{ height: "100%", width: "100%", background: "#070c12" }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════
// LEYENDA DEL MAPA
// ══════════════════════════════════════════════════════════════════════

function MapaLeyenda() {
  const pasos = [
    { color: "#1e3a8a", label: "Bajo" },
    { color: "#0891b2", label: "" },
    { color: "#0d9488", label: "Medio" },
    { color: "#16a34a", label: "" },
    { color: "#ca8a04", label: "Alto" },
    { color: "#ea580c", label: "" },
    { color: "#dc2626", label: "Máx" },
  ];
  const gradient = pasos.map(p => p.color).join(", ");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
      <span style={{ fontSize: 12, color: COLOR.muted, fontFamily: "monospace" }}>Bajo</span>
      <div style={{
        width: 140, height: 10, borderRadius: 4,
        background: `linear-gradient(to right, ${gradient})`,
        flexShrink: 0,
      }} />
      <span style={{ fontSize: 12, color: COLOR.muted, fontFamily: "monospace" }}>Máximo</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8 }}>
        <div style={{
          width: 10, height: 10, borderRadius: 2,
          background: "#252836", opacity: 0.5,
          border: `1px solid #2a2f40`, flexShrink: 0,
        }} />
        <span style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
          Sin reporte
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAPA DE MUNICIPIOS (Leaflet + GeoJSON municipios INEGI)
// ══════════════════════════════════════════════════════════════════════

function MapaCaptacionMunicipio({ municipioData, metrica }) {
  const mapRef     = useRef(null);
  const leafletRef = useRef(null);
  const geoRef     = useRef(null);
  const [geojson,  setGeojson]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  // Cargar GeoJSON de municipios (emrisk/public/municipios_mx.geojson)
  useEffect(() => {
    setLoading(true);
    fetchGeoMunicipios()
      .then(data => { setGeojson(data); setLoading(false); })
      .catch(e => { console.warn("GeoJSON municipios no disponible:", e); setLoading(false); });
  }, []);

  // Índice por cvegeo de 5 dígitos ("01001")
  const dataIdx = React.useMemo(() => {
    if (!municipioData) return {};
    return Object.fromEntries(municipioData.map(d => [d.cvegeo, d]));
  }, [municipioData]);

  // Escala cuantil — igual que MapaCaptacion, evita dominancia de outliers
  const sortedVals = React.useMemo(() => {
    if (!municipioData?.length) return [];
    return municipioData
      .map(d => d[metrica] || 0)
      .filter(v => v > 0)
      .sort((a, b) => a - b);
  }, [municipioData, metrica]);

  const colorScale = useCallback((val) => {
    if (!val || val <= 0 || !sortedVals.length) return "#1a2535";
    let lo = 0, hi = sortedVals.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedVals[mid] < val) lo = mid + 1; else hi = mid;
    }
    const pct = sortedVals.length > 1 ? lo / (sortedVals.length - 1) : 1;
    if (pct < 0.08) return "#1e3a8a";
    if (pct < 0.20) return "#0369a1";
    if (pct < 0.34) return "#0891b2";
    if (pct < 0.50) return "#0d9488";
    if (pct < 0.65) return "#16a34a";
    if (pct < 0.80) return "#ca8a04";
    if (pct < 0.92) return "#ea580c";
    return "#dc2626";
  }, [sortedVals]);

  // Inicializar mapa Leaflet
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    if (leafletRef.current) { leafletRef.current.invalidateSize(); return; }
    leafletRef.current = window.L.map(mapRef.current, {
      center: [23.8, -102.3],
      zoom: 5,
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: true,
      maxBounds: [[13.5, -120.0], [33.5, -85.0]],
      maxBoundsViscosity: 0.8,
      // Canvas en lugar de SVG: imprescindible para 2478 polígonos municipales
      preferCanvas: true,
      renderer: window.L.canvas({ padding: 0.5 }),
    });
    setTimeout(() => leafletRef.current?.invalidateSize(), 50);
    setTimeout(() => leafletRef.current?.invalidateSize(), 300);
  }, []);

  // Actualizar capa GeoJSON cuando cambian datos o métrica
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletRef.current || !geojson || !municipioData) return;
    if (geoRef.current) { geoRef.current.remove(); geoRef.current = null; }

    geoRef.current = L.geoJSON(geojson, {
      style: (feature) => {
        const cvegeo = feature.properties?.CVEGEO || "";
        const d = dataIdx[cvegeo];
        if (!d) {
          // Sin reporte en R2421 — gris apagado, claramente distinto de la escala
          return { fillColor: "#252836", fillOpacity: 0.30, color: "#2a2f40", weight: 0.2 };
        }
        return {
          fillColor:   colorScale(d[metrica] || 0),
          fillOpacity: 0.92,
          color:       "#070c12",
          weight:      0.3,
        };
      },
      onEachFeature: (feature, layer) => {
        const cvegeo     = feature.properties?.CVEGEO || "";
        const d          = dataIdx[cvegeo];
        const nombre_mun = d?.nombre_municipio || feature.properties?.NOMGEO || "—";
        const nombre_edo = d?.nombre_estado || "";
        const val        = d?.[metrica];
        const valLabel   = val == null ? "—"
          : metrica.includes("_b") ? fmtB(val)
          : metrica === "num_cuentas" ? fmtN(val)
          : `$${fmtN(val)}`;

        const tooltipContent = d
          ? `<div style="font-family:monospace;padding:6px 8px;min-width:180px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
              <div style="font-size:12px;font-weight:700;color:#dde4ee;margin-bottom:2px">${nombre_mun}</div>
              ${nombre_edo ? `<div style="font-size:9px;color:#8aa4b8;margin-bottom:5px">${nombre_edo}</div>` : ""}
              <div style="font-size:10px;color:#c8a96e;font-weight:700;margin-bottom:4px">
                ${METRIC_LABELS[metrica] || metrica}: ${valLabel}
              </div>
              <div style="font-size:10px;color:#8aa4b8;line-height:1.6">
                Total: ${fmtB(d.saldo_total_b)}<br/>
                Vista: ${fmtB(d.saldo_vista_b)} · Plazo: ${fmtB(d.saldo_plazo_b)}<br/>
                Cuentas: ${fmtN(d.num_cuentas)}<br/>
                Prom: $${fmtN(d.saldo_promedio_mxn)}
              </div>
            </div>`
          : `<div style="font-family:monospace;padding:6px 8px;min-width:160px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
              <div style="font-size:11px;font-weight:700;color:#8aa4b8;margin-bottom:3px">${feature.properties?.NOMGEO || "—"}</div>
              <div style="font-size:9px;color:#556a7a;line-height:1.5">
                Sin reporte en R2421<br/>
                <span style="color:#3a4a5a">No hay sucursales o captación<br/>registrada en este municipio</span>
              </div>
            </div>`;

        layer.bindTooltip(tooltipContent, { sticky: true, opacity: 1, className: "leaflet-tooltip-emrisk" });
        layer.on("mouseover", () => {
          if (d) layer.setStyle({ weight: 1.5, color: "#c8a96e", fillOpacity: 1 });
          else   layer.setStyle({ weight: 1,   color: "#4a5060", fillOpacity: 0.5 });
        });
        layer.on("mouseout", () => geoRef.current?.resetStyle(layer));
      },
    }).addTo(leafletRef.current);

    try {
      leafletRef.current.fitBounds(geoRef.current.getBounds(), { padding: [12, 12], maxZoom: 7 });
    } catch (_) {}
    leafletRef.current.invalidateSize();
    setTimeout(() => leafletRef.current?.invalidateSize(), 150);
    setTimeout(() => leafletRef.current?.invalidateSize(), 600);
  }, [geojson, dataIdx, metrica, colorScale, municipioData]);

  // IMPORTANTE: el div del mapa SIEMPRE debe estar en el DOM para que
  // Leaflet pueda inicializarse en el primer render. El loading va como overlay.
  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      {loading && !geojson && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#070c12", color: COLOR.muted,
          fontSize: 14, fontFamily: "monospace",
        }}>
          Cargando mapa de municipios…
        </div>
      )}
      {!loading && !geojson && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#070c12", color: COLOR.dim,
          fontSize: 14, fontFamily: "monospace",
        }}>
          GeoJSON de municipios no disponible
        </div>
      )}
      <div ref={mapRef}
        style={{ height: "100%", width: "100%", background: "#070c12" }}
      />
    </div>
  );
}

/**
 * Tabla interactiva de municipios — alternativa al mapa coroplético.
 * Incluye búsqueda por nombre y filtro por estado.
 */
function TablaMunicipios({ municipioData, metrica }) {
  const [busqueda,   setBusqueda]   = useState("");
  const [filtroEdo,  setFiltroEdo]  = useState("");

  if (!municipioData?.length) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center",
                    justifyContent: "center", color: COLOR.muted, fontSize: 14,
                    fontFamily: "monospace" }}>
        Sin datos municipales disponibles.
      </div>
    );
  }

  // Lista de estados únicos para el filtro
  const estados = [...new Set(municipioData.map(d => d.nombre_estado))].sort();

  // Filtrar y ordenar
  const q = busqueda.toLowerCase().trim();
  const filtrados = municipioData
    .filter(d => {
      const matchEdo = !filtroEdo || d.nombre_estado === filtroEdo;
      const matchQ   = !q
        || d.nombre_municipio?.toLowerCase().includes(q)
        || d.nombre_estado?.toLowerCase().includes(q);
      return matchEdo && matchQ;
    })
    .sort((a, b) => (b[metrica] || 0) - (a[metrica] || 0));

  const maxV = filtrados[0]?.[metrica] || 1;

  const valStr = (val) => metrica.includes("_b") ? fmtB(val)
    : metrica === "num_cuentas" ? fmtN(val)
    : `$${fmtN(val)}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* ── Barra de búsqueda + filtro ── */}
      <div style={{
        display: "flex", gap: 8, padding: "8px 12px", flexShrink: 0,
        background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}`,
      }}>
        <input
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar municipio…"
          style={{
            flex: 1, background: COLOR.bg, border: `1px solid ${COLOR.border}`,
            borderRadius: 4, padding: "5px 10px", color: COLOR.text,
            fontSize: 14, fontFamily: "monospace", outline: "none",
          }}
        />
        <select
          value={filtroEdo}
          onChange={e => setFiltroEdo(e.target.value)}
          style={{
            background: COLOR.bg, border: `1px solid ${COLOR.border}`,
            borderRadius: 4, padding: "5px 8px", color: COLOR.muted,
            fontSize: 13, fontFamily: "monospace", cursor: "pointer",
            maxWidth: 160,
          }}
        >
          <option value="">Todos los estados</option>
          {estados.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <span style={{
          fontSize: 12, fontFamily: "monospace", color: COLOR.dim,
          alignSelf: "center", flexShrink: 0,
        }}>
          {filtrados.length.toLocaleString("es-MX")} municipios
        </span>
      </div>

      {/* ── Tabla scrollable ── */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {filtrados.map((d, i) => {
          const val    = d[metrica] || 0;
          const pct    = maxV > 0 ? Math.max(1, (val / maxV) * 100) : 0;
          const isTop3 = i < 3 && !q && !filtroEdo;
          const isTop10 = i < 10 && !q && !filtroEdo;
          return (
            <div key={d.cvegeo || i} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "5px 14px",
              borderBottom: `1px solid ${COLOR.border}22`,
              background: isTop3 ? `${COLOR.gold}06` : "transparent",
            }}>
              {/* Posición */}
              <span style={{
                fontSize: 12, fontFamily: "monospace", flexShrink: 0,
                width: 22, textAlign: "right",
                color: isTop3 ? COLOR.gold : isTop10 ? COLOR.muted : COLOR.dim,
                fontWeight: isTop3 ? 700 : 400,
              }}>{i + 1}</span>
              {/* Nombre + Estado */}
              <div style={{ width: 180, flexShrink: 0, overflow: "hidden" }}>
                <div style={{
                  fontSize: 13, fontFamily: "monospace",
                  color: isTop3 ? COLOR.text : COLOR.muted,
                  fontWeight: isTop3 ? 600 : 400,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{d.nombre_municipio}</div>
                <div style={{
                  fontSize: 11, fontFamily: "monospace", color: COLOR.dim, marginTop: 1,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{d.nombre_estado}</div>
              </div>
              {/* Barra */}
              <div style={{ flex: 1, height: 5, background: COLOR.border,
                            borderRadius: 3, overflow: "hidden", minWidth: 40 }}>
                <div style={{
                  width: `${pct}%`, height: "100%", borderRadius: 3,
                  background: isTop3 ? COLOR.gold : isTop10 ? COLOR.goldDim : "#3d4a1a",
                }} />
              </div>
              {/* Valor */}
              <span style={{
                fontSize: 13, fontFamily: "monospace", flexShrink: 0,
                width: 86, textAlign: "right",
                color: isTop3 ? COLOR.gold : isTop10 ? COLOR.text : COLOR.muted,
                fontWeight: isTop3 ? 700 : 400,
              }}>{valStr(val)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// GRÁFICA FORECAST (Chart.js)
// ══════════════════════════════════════════════════════════════════════

function ForecastChart({ historico, proyecciones }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !window.Chart) return;
    if (!historico?.length && !proyecciones?.length) return;

    // Agrupar por periodo (sumar todas las series)
    const aggHist = {};
    historico.forEach(h => {
      aggHist[h.periodo] = (aggHist[h.periodo] || 0) + h.saldo_historico_mdp;
    });

    const aggProy = {};
    const aggBandL = {};
    const aggBandU = {};
    proyecciones.forEach(p => {
      aggProy[p.periodo]  = (aggProy[p.periodo]  || 0) + p.saldo_proyectado_mdp;
      aggBandL[p.periodo] = (aggBandL[p.periodo] || 0) + p.banda_inferior_mdp;
      aggBandU[p.periodo] = (aggBandU[p.periodo] || 0) + p.banda_superior_mdp;
    });

    const histPeriodos = Object.keys(aggHist).sort();
    const proyPeriodos = Object.keys(aggProy).sort();
    const allPeriodos  = [...new Set([...histPeriodos, ...proyPeriodos])].sort();

    const histData = allPeriodos.map(p => aggHist[p] != null ? aggHist[p] / 1e3 : null);
    const proyData = allPeriodos.map(p => aggProy[p] != null ? aggProy[p] / 1e3 : null);
    const bandLData = allPeriodos.map(p => aggBandL[p] != null ? aggBandL[p] / 1e3 : null);
    const bandUData = allPeriodos.map(p => aggBandU[p] != null ? aggBandU[p] / 1e3 : null);

    const labels = allPeriodos.map(p => `${p.slice(0,4)}-${p.slice(4)}`);

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new window.Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Histórico",
            data: histData,
            borderColor: COLOR.blue,
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 2,
            tension: 0.3,
          },
          {
            label: "Proyección",
            data: proyData,
            borderColor: COLOR.green,
            backgroundColor: "rgba(34,197,94,0.1)",
            borderWidth: 2,
            borderDash: [5, 3],
            pointRadius: 3,
            fill: false,
            tension: 0.3,
          },
          {
            label: "Banda superior",
            data: bandUData,
            borderColor: "rgba(34,197,94,0.3)",
            backgroundColor: "rgba(34,197,94,0.08)",
            borderWidth: 1,
            pointRadius: 0,
            fill: "+1",
            tension: 0.3,
          },
          {
            label: "Banda inferior",
            data: bandLData,
            borderColor: "rgba(34,197,94,0.3)",
            backgroundColor: "rgba(34,197,94,0.08)",
            borderWidth: 1,
            pointRadius: 0,
            fill: false,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: { font: { size: 12, family: "monospace" }, boxWidth: 12,
                      color: "#8aa4b8" },
          },
          tooltip: { mode: "index", intersect: false },
        },
        scales: {
          x: {
            ticks: { font: { size: 11, family: "monospace" }, maxRotation: 45, color: "#8aa4b8" },
            grid: { color: "#1a2535" },
          },
          y: {
            ticks: { font: { size: 11, family: "monospace" }, color: "#8aa4b8" },
            grid: { color: "#1a2535" },
            title: { display: true, text: "Miles de MDP", font: { size: 11, family: "monospace" }, color: "#8aa4b8" },
          },
        },
        interaction: { mode: "nearest", axis: "x" },
        backgroundColor: "#111b28",
      },
    });
  }, [historico, proyecciones]);

  return (
    <div style={{ background: COLOR.card, borderRadius: 6, padding: "8px 4px",
                  border: `1px solid ${COLOR.border}` }}>
      <canvas ref={canvasRef} style={{ height: 200 }} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// GENERADOR DE HTML PARA DESCARGA
// ══════════════════════════════════════════════════════════════════════

function generarHTMLExport(reporte) {
  const s   = reporte.secciones || {};
  const k   = s.resumen?.kpis  || {};
  const per = String(reporte.periodo);
  const perFmt = `${per.slice(0,4)}-${per.slice(4)}`;
  const ent = reporte.nombre_entidad || "Banca Múltiple";

  // Helpers
  const fB  = (n, d=2) => `${(n ?? 0).toFixed(d)} mmdp`;
  const fN  = n => (n ?? 0).toLocaleString("es-MX");
  const fP  = n => n == null ? "—" : `${n >= 0 ? "+" : ""}${(n ?? 0).toFixed(1)}%`;
  const esc = v => String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const C = {
    bg:"#070c12", surface:"#0d1520", card:"#111b28", border:"#1a2535",
    text:"#dde4ee", muted:"#8aa4b8", dim:"#556a7a",
    gold:"#c8a96e", goldDim:"#8a6d3b",
    green:"#34d399", red:"#f87171", blue:"#60a5fa", amber:"#fbbf24",
  };

  const kpiCard = (label, value, delta, dl) =>
    `<div style="background:${C.card};border-radius:6px;padding:12px 16px;border:1px solid ${C.border};flex:1;min-width:160px;">
      <div style="font-size:10px;color:${C.muted};font-weight:700;font-family:monospace;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;">${esc(label)}</div>
      <div style="font-size:22px;font-weight:800;color:${C.gold};font-family:monospace;">${esc(value)}</div>
      ${delta != null ? `<div style="font-size:11px;margin-top:5px;font-family:monospace;color:${delta>0?C.green:delta<0?C.red:C.muted};">${delta>0?"▲":delta<0?"▼":"─"} ${fP(delta)}${dl?` <span style="color:${C.dim}">${esc(dl)}</span>`:""}</div>` : ""}
    </div>`;

  const insightBox = text => !text ? "" :
    `<div style="background:${C.gold}0d;border:1px solid ${C.goldDim};border-radius:6px;padding:10px 14px;font-size:12px;line-height:1.7;color:${C.text};margin-top:10px;">
      <div style="font-size:9px;font-weight:700;font-family:monospace;letter-spacing:0.05em;background:${C.gold}22;color:${C.gold};display:inline-block;padding:1px 8px;border-radius:4px;margin-bottom:6px;border:1px solid ${C.goldDim};">✦ Comentario analítico</div>
      <div style="white-space:pre-wrap;">${esc(text)}</div>
    </div>`;

  const secHeader = (icon, title) =>
    `<div style="padding:10px 0;margin-bottom:14px;border-bottom:2px solid ${C.gold}44;">
      <span style="font-size:16px;margin-right:8px;">${icon}</span>
      <span style="font-weight:700;font-size:13px;color:${C.gold};font-family:monospace;letter-spacing:0.05em;text-transform:uppercase;">${esc(title)}</span>
    </div>`;

  const barRow = (label, pct, right, color) =>
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <div style="width:130px;font-size:11px;color:${C.muted};font-family:monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex-shrink:0;">${esc(label)}</div>
      <div style="flex:1;height:6px;background:${C.border};border-radius:4px;overflow:hidden;"><div style="width:${Math.min(100,Math.max(1,pct))}%;height:100%;background:${color||C.gold};border-radius:4px;"></div></div>
      <div style="font-size:11px;color:${C.muted};width:80px;font-family:monospace;text-align:right;flex-shrink:0;">${esc(right)}</div>
    </div>`;

  const thS = `padding:8px 12px;text-align:left;color:${C.muted};font-weight:600;border-bottom:1px solid ${C.border};font-size:10px;font-family:monospace;`;
  const tdS = `padding:7px 12px;font-size:10px;font-family:monospace;`;

  const rankColor = (i, total) => {
    const p = total > 1 ? (total-1-i)/(total-1) : 1;
    return p<0.08?"#1e3a8a":p<0.20?"#0369a1":p<0.34?"#0891b2":p<0.50?"#0d9488":p<0.65?"#16a34a":p<0.80?"#ca8a04":p<0.92?"#ea580c":"#dc2626";
  };

  // ── 1. RESUMEN ──────────────────────────────────────────────────────
  const secResumen = `<section style="margin-bottom:32px;">
    ${secHeader("📋","Resumen ejecutivo")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Captación tradicional",  fB(k.total_b), k.delta_total_yoy,  "YoY")}
      ${kpiCard("Captación vista",  fB(k.vista_b), k.delta_vista_yoy,  "YoY")}
      ${kpiCard("Captación plazo",  fB(k.plazo_b), k.delta_plazo_yoy,  "YoY")}
      ${kpiCard("Total cuentas", `${(k.num_cuentas_m??0).toFixed(1)} M`, k.delta_cuentas_mom, "MoM")}
    </div>
    <div style="font-size:10px;color:${C.muted};line-height:1.5;margin-bottom:12px;">La <b>Captación tradicional</b> corresponde a los productos de depósito del R24 B-2421: depósitos de exigibilidad inmediata + a plazo + cuenta global (saldos al cierre). <b>No incluye títulos de crédito emitidos</b> (no se reportan en el R24 B-2421); para instituciones sin títulos emitidos coincide con la Captación Tradicional del balance CNBV. Excluye flujos del periodo (cuentas incorporadas, reactivadas y prescritas de la Cuenta Global) y saldos a favor de tarjeta de crédito. A nivel de sistema es la captación tradicional en depósitos <b>cubierta por el R24 B-2421</b>; no equivale a la "Captación Total" del balance.</div>
    ${insightBox(s.resumen?.narrativa)}
    ${s.comparativo ? `
    <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-top:14px;">
      <div style="padding:10px 16px;background:${C.surface};border-bottom:1px solid ${C.border};"><span style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};letter-spacing:0.05em;">${esc((reporte.nombre_entidad||"INSTITUCIÓN").toUpperCase())} VS SISTEMA</span></div>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="background:${C.surface};">${["Indicador",esc(reporte.nombre_entidad||"Banco"),"Sistema","Dif. (pp)"].map(h=>`<th style="${thS}">${h}</th>`).join("")}</tr></thead>
        <tbody>
          ${[["Var. mensual tradicional","delta_total_mom"],["Var. anual tradicional","delta_total_yoy"],["Var. anual vista","delta_vista_yoy"],["Var. anual plazo","delta_plazo_yoy"]].map(([lbl,kk])=>{
            const vb=s.comparativo.banco?.[kk], vs=s.comparativo.sistema?.[kk];
            const dif=(vb!=null&&vs!=null)?vb-vs:null;
            const dc=dif==null?C.muted:dif>=0?C.green:C.red;
            return `<tr style="border-bottom:1px solid ${C.border}22;"><td style="${tdS}color:${C.muted};">${lbl}</td><td style="${tdS}color:${C.gold};font-weight:700;">${vb!=null?vb.toFixed(1)+"%":"—"}</td><td style="${tdS}color:${C.muted};">${vs!=null?vs.toFixed(1)+"%":"—"}</td><td style="${tdS}color:${dc};">${dif!=null?(dif>0?"+":"")+dif.toFixed(1)+" pp":"—"}</td></tr>`;
          }).join("")}
        </tbody>
      </table>
      <div style="padding:10px 16px;font-size:10px;color:${C.muted};line-height:1.6;border-top:1px solid ${C.border};">Participación de ${esc(reporte.nombre_entidad||"la institución")} en el sistema — captación tradicional ${(s.comparativo.participacion?.captacion_total_pct??0).toFixed(2)}%, vista ${(s.comparativo.participacion?.vista_pct??0).toFixed(2)}%, plazo ${(s.comparativo.participacion?.plazo_pct??0).toFixed(2)}%, cuentas ${(s.comparativo.participacion?.cuentas_pct??0).toFixed(2)}%.</div>
    </div>` : ""}
  </section>`;

  // ── 1b. CONTRIBUCIÓN AL CAMBIO ──────────────────────────────────────
  const contrib = s.contribucion;
  const secContribucion = !contrib ? "" : (() => {
    const sis = contrib.sistema || {};
    const insts = (contrib.instituciones || []).slice(0, 10);
    const cm = contrib.concentracion?.mom;
    const bancoBox = contrib.banco?.encontrado ? `
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;padding:12px 16px;margin-bottom:12px;">
        <div style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};letter-spacing:0.05em;margin-bottom:6px;">POSICIÓN DE ${esc((reporte.nombre_entidad||"").toUpperCase())}</div>
        <div style="font-size:11px;color:${C.muted};line-height:1.7;">Posición ${contrib.banco.posicion} de ${contrib.banco.de_total} por contribución mensual · contribución ${contrib.banco.contrib_mom_pct!=null?contrib.banco.contrib_mom_pct.toFixed(1)+"%":"—"} · participación ${(contrib.banco.participacion_pct??0).toFixed(2)}% · rol: ${esc(contrib.banco.rol||"")}.</div>
      </div>` : "";
    const concTxt = (cm && !contrib.delta_mom_cero) ? `
      <div style="font-size:11px;color:${C.muted};line-height:1.6;margin-bottom:12px;">Concentración del movimiento: las 3 mayores en el mismo sentido explican ${(cm.top3_pct??0).toFixed(1)}% y las 5 mayores ${(cm.top5_pct??0).toFixed(1)}% de la ${esc(cm.sentido||"variación")} del sistema (magnitud positiva). Puede exceder 100% por movimientos compensatorios de otras instituciones.</div>` :
      (contrib.delta_mom_cero ? `<div style="font-size:11px;color:${C.amber};line-height:1.6;margin-bottom:12px;">Cambio mensual del sistema prácticamente nulo; se muestran deltas en MDP sin porcentajes de contribución.</div>` : "");
    const filas = insts.map((i,k)=>{
      const pos=(i.delta_mom_mdp||0)>=0; const col=pos?C.green:C.red;
      const colY=(i.delta_yoy_mdp||0)>=0?C.green:C.red;
      return `<tr style="border-bottom:1px solid ${C.border}22;background:${k%2===0?"transparent":C.surface+"80"};">
        <td style="${tdS}color:${C.text};">${esc(i.nombre)}</td>
        <td style="${tdS}color:${C.muted};">${fN(Math.round(i.s_act||0))}</td>
        <td style="${tdS}color:${C.muted};">${fN(Math.round(i.s_ant||0))}</td>
        <td style="${tdS}color:${C.muted};">${fN(Math.round(i.s_yoy||0))}</td>
        <td style="${tdS}color:${col};">${(i.delta_mom_mdp||0)>0?"+":""}${fN(Math.round(i.delta_mom_mdp||0))}</td>
        <td style="${tdS}color:${C.muted};">${i.delta_mom_pct!=null?i.delta_mom_pct.toFixed(1)+"%":"—"}</td>
        <td style="${tdS}color:${col};font-weight:700;">${i.contrib_mom_pct!=null?(i.contrib_mom_pct>0?"+":"")+i.contrib_mom_pct.toFixed(1)+"%":"—"}</td>
        <td style="${tdS}color:${colY};">${(i.delta_yoy_mdp||0)>0?"+":""}${fN(Math.round(i.delta_yoy_mdp||0))}</td>
        <td style="${tdS}color:${C.muted};">${i.delta_yoy_pct!=null?i.delta_yoy_pct.toFixed(1)+"%":"—"}</td>
        <td style="${tdS}color:${C.muted};">${i.contrib_yoy_pct!=null?i.contrib_yoy_pct.toFixed(1)+"%":"—"}</td>
        <td style="${tdS}color:${C.gold};">${(i.participacion_pct??0).toFixed(2)}%</td>
      </tr>`;}).join("");
    return `<section style="margin-bottom:32px;">
      ${secHeader("🏦","Instituciones que explican el cambio")}
      <div style="display:flex;gap:10px;margin-bottom:14px;flex-wrap:wrap;">
        ${kpiCard("Δ mensual sistema", `${fN(Math.round(sis.delta_mom_mdp||0))} MDP`, sis.delta_mom_pct, "MoM")}
        ${kpiCard("Δ anual sistema", `${fN(Math.round(sis.delta_yoy_mdp||0))} MDP`, sis.delta_yoy_pct, "YoY")}
      </div>
      ${insightBox(contrib.narrativa)}
      ${concTxt}
      ${bancoBox}
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;">
        <div style="padding:10px 16px;background:${C.surface};border-bottom:1px solid ${C.border};"><span style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};letter-spacing:0.05em;">TOP 10 POR CONTRIBUCIÓN AL CAMBIO MENSUAL</span></div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:${C.surface};">${["Institución","Saldo","Saldo ant.","Saldo YoY","Δ MoM (MDP)","Δ MoM %","Contrib. MoM","Δ YoY (MDP)","Δ YoY %","Contrib. YoY","Part. %"].map(h=>`<th style="${thS}">${h}</th>`).join("")}</tr></thead>
          <tbody>${filas}</tbody>
        </table>
      </div>
    </section>`;
  })();

  // ── 2. VISTA ────────────────────────────────────────────────────────
  const vista = s.vista;
  const secVista = !vista ? "" : `<section style="margin-bottom:32px;">
    ${secHeader("💧","Captación a la vista")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Vista activas",         fB(vista.saldo_activas_b))}
      ${kpiCard("Vista inactivas/Global", fB(vista.saldo_inactivas_b))}
      ${kpiCard("Total vista",           fB(vista.saldo_total_b))}
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:14px;">
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px;">Por producto (cuentas activas)</div>
        ${(vista.por_producto||[]).map((p,i) => barRow(
          p.nombre.replace(/Cuentas Transaccionales de Bajo Riesgo /g,"BR "),
          (vista.por_producto[0]?.saldo_b||0) > 0 ? (p.saldo_b/(vista.por_producto[0].saldo_b))*100 : 0,
          `${fB(p.saldo_b)} (${p.pct}%)`,
          [C.gold,C.blue,"#a78bfa",C.green,C.amber][i]||C.muted
        )).join("")}
        <div style="margin-top:10px;font-size:11px;color:${C.muted};font-family:monospace;">Saldo promedio: <strong style="color:${C.text};">$${fN(vista.saldo_promedio_mxn)}</strong></div>
      </div>
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px;">Por tenedor (cuentas activas)</div>
        ${(vista.por_tenedor||[]).map((t,i) => barRow(
          t.nombre.replace("Persona ","").replace(" nacional",""),
          (vista.por_tenedor[0]?.saldo_b||0) > 0 ? (t.saldo_b/vista.por_tenedor[0].saldo_b)*100 : 0,
          fB(t.saldo_b),
          ["#a78bfa",C.blue,C.gold][i]||C.dim
        )).join("")}
      </div>
    </div>
    ${vista.nota_base ? `<div style="font-size:10px;color:${C.muted};line-height:1.5;margin-bottom:12px;">${esc(vista.nota_base)}</div>` : ""}
    ${insightBox(s.vista?.insight)}
  </section>`;

  // ── 3. PLAZO ────────────────────────────────────────────────────────
  const plazo = s.plazo;
  const secPlazo = !plazo ? "" : `<section style="margin-bottom:32px;">
    ${secHeader("📅","Captación a plazo")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:14px;">
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px;">Por moneda</div>
        ${(plazo.por_moneda||[]).map((m,i) => barRow(
          m.nombre,
          (plazo.por_moneda[0]?.saldo_b||0) > 0 ? (m.saldo_b/plazo.por_moneda[0].saldo_b)*100 : 0,
          `${fB(m.saldo_b)} (${m.pct}%)`,
          i===0 ? C.gold : C.amber
        )).join("")}
        <div style="margin-top:10px;font-size:11px;color:${C.muted};font-family:monospace;">Saldo promedio: <strong style="color:${C.text};">$${fN(plazo.saldo_promedio_mxn)}</strong></div>
      </div>
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px;">Por tenedor</div>
        ${(plazo.por_tenedor||[]).map((t,i) => barRow(
          t.nombre.replace("Persona ","").replace(" nacional",""),
          (plazo.por_tenedor[0]?.saldo_b||0) > 0 ? (t.saldo_b/plazo.por_tenedor[0].saldo_b)*100 : 0,
          fB(t.saldo_b),
          [C.gold,C.amber,C.dim][i]||C.dim
        )).join("")}
      </div>
    </div>
    ${insightBox(s.plazo?.insight)}
  </section>`;

  // ── 4. TERRITORIO ───────────────────────────────────────────────────
  const terr    = s.territorio || [];
  const terrJSON    = JSON.stringify(terr);
  const isoMapJSON  = JSON.stringify(MX_ISO_TO_CVE);
  // Métricas disponibles (las mismas que en el app React)
  const TERR_METRICS = [
    { key: "saldo_total_b",    label: "Saldo total",   fmt: "b" },
    { key: "saldo_vista_b",    label: "Vista",         fmt: "b" },
    { key: "saldo_plazo_b",    label: "Plazo",         fmt: "b" },
    { key: "num_cuentas",      label: "Cuentas",       fmt: "n" },
    { key: "saldo_promedio_mxn", label: "Saldo prom.", fmt: "mxn" },
  ];
  const terrMetricsJSON = JSON.stringify(TERR_METRICS);

  const secTerritorio = `<section style="margin-bottom:32px;">
    ${secHeader("🗺️","Distribución territorial (por estado)")}

    <!-- Toggle de métricas -->
    <div style="display:flex;gap:6px;flex-wrap:wrap;padding:10px 12px;background:${C.surface};border:1px solid ${C.border};border-radius:8px;margin-bottom:14px;align-items:center;">
      <span style="font-size:10px;color:${C.dim};font-family:monospace;margin-right:4px;">MÉTRICA</span>
      ${TERR_METRICS.map(m => `
      <button class="terr-btn" data-metric="${m.key}"
        onclick="switchTerrMetric('${m.key}')"
        style="padding:4px 12px;border-radius:4px;font-size:10px;font-family:monospace;cursor:pointer;
               border:1px solid ${m.key==='saldo_total_b'?C.goldDim:C.border};
               background:${m.key==='saldo_total_b'?C.gold+'22':'transparent'};
               color:${m.key==='saldo_total_b'?C.gold:C.muted};
               font-weight:${m.key==='saldo_total_b'?700:400};">
        ${m.label}
      </button>`).join("")}
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <!-- MAPA -->
      <div>
        <div style="font-size:10px;font-family:monospace;color:${C.muted};margin-bottom:6px;">
          Mostrando: <span id="terr-metric-label" style="color:${C.gold};font-weight:700;">Saldo total</span>
        </div>
        <div id="mapa-estados" style="height:380px;border-radius:8px;border:1px solid ${C.border};background:#0a1118;"></div>
        <div style="display:flex;gap:8px;align-items:center;margin-top:8px;font-size:10px;font-family:monospace;color:${C.muted};">
          <span>Bajo</span>
          <div style="width:140px;height:8px;background:linear-gradient(to right,#1e3a8a,#0891b2,#0d9488,#16a34a,#ca8a04,#ea580c,#dc2626);border-radius:4px;"></div>
          <span>Máximo</span>
          <div style="width:10px;height:10px;background:#252836;opacity:0.5;border-radius:2px;margin-left:8px;"></div>
          <span>Sin datos</span>
        </div>
      </div>
      <!-- RANKING -->
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;display:flex;flex-direction:column;">
        <div style="padding:8px 14px;background:${C.surface};border-bottom:1px solid ${C.border};font-size:11px;font-family:monospace;color:${C.text};font-weight:600;">
          Ranking — <span id="ranking-metric-label">Saldo total</span>
        </div>
        <div id="ranking-list" style="flex:1;overflow-y:auto;max-height:380px;"></div>
      </div>
    </div>
  </section>`;

  // ── 5. INCLUSIÓN ────────────────────────────────────────────────────
  const incl = s.inclusion;
  const secInclusion = !incl ? "" : (() => {
    const niveles    = incl.por_nivel  || [];
    const generos    = incl.por_genero || [];
    const totalCtas  = niveles.reduce((a,n) => a+(n.num_cuentas||0), 0);
    const totalSaldo = niveles.reduce((a,n) => a+(n.saldo_b||0), 0);
    const maxCtas    = Math.max(...niveles.map(n => n.num_cuentas||0), 1);
    const saldoProm  = totalCtas > 0 ? Math.round((totalSaldo*1e9)/totalCtas) : 0;
    return `<section style="margin-bottom:32px;">
      ${secHeader("🤝","Inclusión financiera")}
      <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
        ${kpiCard("Cuentas inclusión", fN(totalCtas))}
        ${kpiCard("Saldo inclusión",   fB(totalSaldo))}
        ${kpiCard("Saldo promedio",    `$${fN(saldoProm)}`)}
      </div>
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;padding:16px 20px;margin-bottom:14px;">
        <div style="font-size:10px;font-weight:700;color:${C.text};font-family:monospace;letter-spacing:0.05em;margin-bottom:14px;">PRODUCTOS DE INCLUSIÓN FINANCIERA</div>
        ${incl.nota_universo_nivel ? `<div style="font-size:10px;color:${C.muted};line-height:1.5;margin-bottom:12px;">${esc(incl.nota_universo_nivel)}</div>` : ""}
        ${niveles.map(n => {
          const pct = (n.num_cuentas/maxCtas)*100;
          return `<div style="margin-bottom:10px;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
              <span style="font-size:10px;font-family:monospace;color:${C.muted};">${esc(n.producto||n.descripcion_producto)}</span>
              <span style="font-size:10px;font-family:monospace;color:${C.gold};font-weight:700;">${fN(n.num_cuentas)}</span>
            </div>
            <div style="height:5px;background:${C.border};border-radius:3px;overflow:hidden;">
              <div style="width:${Math.max(1,pct)}%;height:100%;background:${C.gold};border-radius:3px;"></div>
            </div>
          </div>`;
        }).join("")}
      </div>
      ${generos.length > 0 ? `
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;padding:16px 20px;margin-bottom:14px;">
        <div style="font-size:10px;font-weight:700;color:${C.text};font-family:monospace;letter-spacing:0.05em;margin-bottom:14px;">DISTRIBUCIÓN POR GÉNERO</div>
        ${incl.nota_universo_genero ? `<div style="font-size:10px;color:${C.amber};line-height:1.5;margin-bottom:12px;">${esc(incl.nota_universo_genero)}</div>` : ""}
        <div style="display:flex;gap:16px;flex-wrap:wrap;">
          ${generos.map(g => `
          <div style="flex:1;min-width:160px;background:${C.surface};border:1px solid ${C.border};border-radius:6px;padding:12px 16px;">
            <div style="font-size:18px;margin-bottom:4px;">${(g.genero||g.descripcion_genero)==="Mujer"?"♀️":"♂️"}</div>
            <div style="font-size:12px;font-family:monospace;color:${C.text};font-weight:700;">${esc(g.genero||g.descripcion_genero)}</div>
            <div style="font-size:11px;font-family:monospace;color:${C.muted};margin-top:6px;">${fN(g.num_cuentas)} cuentas</div>
            <div style="font-size:11px;font-family:monospace;color:${C.gold};">${fB(g.saldo_b)}</div>
          </div>`).join("")}
        </div>
      </div>` : ""}
      ${insightBox(s.inclusion?.insight)}
    </section>`;
  })();

  // ── 6. CONCENTRACIÓN ────────────────────────────────────────────────
  const conc = s.concentracion;
  const g = s.concentracion_geografica;
  const secGeo = !g ? "" : `
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-top:14px;margin-bottom:14px;">
        <div style="padding:10px 16px;background:${C.surface};border-bottom:1px solid ${C.border};"><span style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};letter-spacing:0.05em;">CONCENTRACIÓN GEOGRÁFICA INTERNA — ${esc((reporte.nombre_entidad||"").toUpperCase())}</span></div>
        <div style="display:flex;gap:10px;flex-wrap:wrap;padding:14px 16px;">
          ${kpiCard("HHI geográfico", (g.hhi??0).toFixed(0))}
          ${kpiCard("Top estado ("+esc(g.top_estado||"?")+")", (g.top_estado_pct??0).toFixed(1)+"%")}
          ${kpiCard("Top-3 estados", (g.top3_pct??0).toFixed(1)+"%")}
          ${kpiCard("Top-5 estados", (g.top5_pct??0).toFixed(1)+"%")}
        </div>
        <div style="padding:0 16px 12px;font-size:10px;color:${C.muted};line-height:1.5;">Distribución de la captación de ${esc(reporte.nombre_entidad||"la institución")} entre estados (riesgo de concentración geográfica del propio banco).</div>
      </div>`;
  const secConcentracion = (!conc?.filas?.length && !g) ? "" :
    `<section style="margin-bottom:32px;">
      ${secHeader("🔍","Concentración de mercado (HHI)")}
      ${secGeo}
      ${insightBox(conc?.insight)}
      ${!conc?.filas?.length ? "" : `<div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-top:14px;">
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:${C.surface};">
            ${["Estado","Producto","HHI","Δ interanual (pts)","Dirección","Mayor participación","Participación"].map(h=>`<th style="${thS}">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${conc.filas.map((f,i) => {
              const hhi   = f.hhi_actual||f.hhi||0;
              const nivel = hhi>2500?C.red:hhi>1500?C.amber:C.green;
              const pct   = f.share_dominante_pct ?? f.participacion_dominante;
              const dHhi  = f.delta_hhi;
              const dCol  = dHhi==null?C.muted:dHhi>=100?C.red:dHhi<=-100?C.green:C.muted;
              return `<tr style="border-bottom:1px solid ${C.border}22;background:${i%2===0?"transparent":C.surface+"80"};">
                <td style="${tdS}color:${C.muted};">${esc(f.descripcion_estado||f.nombre_estado||"—")}</td>
                <td style="${tdS}color:${C.text};">${esc(f.descripcion_producto||"—")}</td>
                <td style="${tdS}"><span style="color:${nivel};font-weight:700;">${fN(hhi)}</span></td>
                <td style="${tdS}color:${dCol};font-weight:600;">${dHhi!=null?`${dHhi>0?"+":""}${fN(dHhi)} pts`:"—"}</td>
                <td style="${tdS}color:${C.muted};">${esc(f.direccion||"—")}</td>
                <td style="${tdS}color:${C.gold};">${esc(f.banco_dominante||"—")}</td>
                <td style="${tdS}color:${C.muted};">${pct!=null?`${(pct??0).toFixed(1)}%`:"—"}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>
      <div style="font-size:10px;color:${C.muted};line-height:1.5;margin-top:10px;">Las categorías de concentración (competitivo, moderada, alta, cuasi-monopolio) corresponden a criterios analíticos de EMRISK sobre el índice HHI y no constituyen umbrales regulatorios ni criterios de supervisión establecidos por la CNBV.</div>`}
    </section>`;

  // ── 7. ANOMALÍAS ────────────────────────────────────────────────────
  const anom = s.anomalias;
  const secAnomalias = !anom?.filas?.length ? "" :
    `<section style="margin-bottom:32px;">
      ${secHeader("🚨","Anomalías estadísticas")}
      ${insightBox(anom.insight)}
      <div style="font-size:10px;color:${C.muted};line-height:1.5;margin-top:10px;">Metodología: el z-score se calcula sobre la variación mensual (Δ) del saldo de cada serie entidad × estado × producto. La media y la desviación estándar se estiman sobre todo el histórico previo al periodo analizado (mínimo 12 meses; se excluyen series con desviación estándar cero o menos de 12 observaciones). Umbrales de severidad: |z| ≥ 3.0 crítica, 2.0 ≤ |z| < 3.0 moderada (se listan casos con |z| ≥ 2.0). "Crítica" indica magnitud estadística, no irregularidad.</div>
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-top:14px;">
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:${C.surface};">
            ${["Entidad","Estado","Producto","Periodo","Z-score","Δ (mdp)"].map(h=>`<th style="${thS}">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${anom.filas.map((f,i) => {
              const z      = Math.abs(f.z_score||0);
              const zColor = z>3?C.red:z>2?C.amber:C.muted;
              return `<tr style="border-bottom:1px solid ${C.border}22;background:${i%2===0?"transparent":C.surface+"80"};">
                <td style="${tdS}color:${C.text};">${esc(f.nombre_entidad||"—")}</td>
                <td style="${tdS}color:${C.muted};">${esc(f.descripcion_estado||"—")}</td>
                <td style="${tdS}color:${C.muted};">${esc(f.descripcion_producto||"—")}</td>
                <td style="${tdS}color:${C.muted};">${esc(String(f.periodo||"—"))}</td>
                <td style="${tdS}"><span style="color:${zColor};font-weight:700;">${f.z_score!=null?(f.z_score).toFixed(2):"—"}</span></td>
                <td style="${tdS}color:${C.gold};">${f.delta_mensual_mdp!=null?fN(Math.round(f.delta_mensual_mdp)):"—"}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>
      ${(() => {
        const M = s.anomalias?.materialidad;
        if (!M) return "";
        const seg = M.seguimiento || {}; const mz = M.matriz || {}; const cmp = M.comparativo || {};
        const cc = (c) => c==="Prioridad crítica"?C.red:c==="Material"?C.amber:c==="Revisión"?C.gold:C.muted;
        const matRows = ["Crítica","Moderada"].map(sev => `<tr style="border-bottom:1px solid ${C.border}22;"><td style="${tdS}color:${C.text};font-weight:700;">z ${sev}</td>${["Alta","Media","Baja"].map(mm=>{const v=mz[`${sev}|${mm}`]||0;const hot=sev==="Crítica"&&mm==="Alta";return `<td style="${tdS}color:${hot&&v>0?C.red:v>0?C.text:C.muted};font-weight:${hot?700:400};">${v}</td>`;}).join("")}</tr>`).join("");
        const priRows = (M.anomalias||[]).slice(0,20).map((a,k)=>`<tr style="border-bottom:1px solid ${C.border}22;background:${k%2===0?"transparent":C.surface+"80"};">
          <td style="${tdS}color:${C.text};">${esc(a.nombre_entidad)}</td>
          <td style="${tdS}color:${C.muted};">${esc(a.descripcion_estado)}</td>
          <td style="${tdS}color:${C.muted};">${esc((a.descripcion_producto||"").replace("Cuentas Transaccionales de Bajo Riesgo ","BR "))}</td>
          <td style="${tdS}color:${C.muted};">${fN(Math.round(a.saldo_mdp||0))}</td>
          <td style="${tdS}color:${C.muted};">${a.saldo_ant_mdp!=null?fN(Math.round(a.saldo_ant_mdp)):"—"}</td>
          <td style="${tdS}color:${Math.abs(a.z_score||0)>=3?C.red:C.amber};font-weight:700;">${a.z_score!=null?a.z_score.toFixed(1):"—"}</td>
          <td style="${tdS}color:${a.severidad_estadistica==="Crítica"?C.red:C.muted};">${esc(a.severidad_estadistica)}</td>
          <td style="${tdS}color:${(a.delta_mdp||0)>=0?C.green:C.red};">${(a.delta_mdp||0)>0?"+":""}${fN(Math.round(a.delta_mdp||0))}</td>
          <td style="${tdS}color:${C.muted};">${a.delta_pct_saldo_ant!=null?a.delta_pct_saldo_ant.toFixed(1)+"%":"—"}</td>
          <td style="${tdS}color:${C.muted};">${a.delta_pct_captacion_inst!=null?a.delta_pct_captacion_inst.toFixed(2)+"%":"—"}</td>
          <td style="${tdS}color:${C.muted};">${a.delta_pct_plaza_prod!=null?a.delta_pct_plaza_prod.toFixed(1)+"%":"—"}</td>
          <td style="${tdS}color:${a.materialidad==="Alta"?C.amber:C.muted};">${a.materialidad}</td>
          <td style="${tdS}color:${cc(a.clasificacion_combinada)};font-weight:600;">${esc(a.clasificacion_combinada)}</td>
          <td style="${tdS}color:${C.muted};">${esc(a.estado_seguimiento)}</td>
          <td style="${tdS}color:${C.muted};">${a.meses_consecutivos}</td>
        </tr>`).join("");
        const _rp = (p) => (p||"").replace("Cuentas Transaccionales de Bajo Riesgo ","BR ");
        const resTxt = (M.resueltas?.length||M.sin_info?.length) ? `<div style="font-size:10px;color:${C.muted};line-height:1.6;margin-top:8px;">${M.resueltas?.length?`<div><b style="color:${C.green};">Resueltas (${M.resueltas.length}):</b> ${M.resueltas.slice(0,8).map(r=>esc(r.nombre_entidad+"/"+r.descripcion_estado+"/"+_rp(r.descripcion_producto)+(r.z_prev!=null?` (z previo ${r.z_prev.toFixed(1)} → actual ${r.z_actual!=null?r.z_actual.toFixed(1):"—"}, por debajo del umbral)`:""))).join(" · ")}${M.resueltas.length>8?" …":""}</div>`:""}${M.sin_info?.length?`<div style="margin-top:3px;"><b>Sin información suficiente (${M.sin_info.length}):</b> ${M.sin_info.slice(0,8).map(r=>esc(r.nombre_entidad+"/"+r.descripcion_estado+"/"+_rp(r.descripcion_producto))).join(" · ")}${M.sin_info.length>8?" …":""} — sin dato en el periodo actual.</div>`:""}</div>` : "";
        return `
      <div style="margin-top:18px;font-size:11px;font-weight:700;font-family:monospace;color:${C.text};letter-spacing:0.05em;">MATERIALIDAD ECONÓMICA Y SEGUIMIENTO</div>
      ${insightBox(M.narrativa)}
      <div style="font-size:11px;color:${C.muted};margin:8px 0;">Nuevas ${seg.nuevas??0} · Recurrentes ${seg.recurrentes??0} · Persistentes ${seg.persistentes??0} · Resueltas ${seg.resueltas??0} · Sin info ${seg.sin_info??0}${cmp.n_anterior!=null?` · Mes previo ${cmp.n_anterior}`:""}</div>
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-bottom:12px;">
        <div style="padding:8px 16px;background:${C.surface};border-bottom:1px solid ${C.border};"><span style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};">MATRIZ SEVERIDAD × MATERIALIDAD</span></div>
        <table style="width:100%;border-collapse:collapse;"><thead><tr style="background:${C.surface};">${["","Mat. Alta","Media","Baja"].map(h=>`<th style="${thS}">${h}</th>`).join("")}</tr></thead><tbody>${matRows}</tbody></table>
      </div>
      ${priRows ? `<div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;">
        <div style="padding:8px 16px;background:${C.surface};border-bottom:1px solid ${C.border};"><span style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};">ANOMALÍAS PRIORIZADAS (materialidad, luego z)</span></div>
        <table style="width:100%;border-collapse:collapse;"><thead><tr style="background:${C.surface};">${["Institución","Estado","Producto","Saldo","Saldo ant.","z","Sev.","Δ MDP","% saldo prev","% capt.","% plaza","Materialidad","Clasificación","Seguim.","Meses"].map(h=>`<th style="${thS}">${h}</th>`).join("")}</tr></thead><tbody>${priRows}</tbody></table>
        <div style="padding:8px 16px;font-size:9px;color:${C.muted};line-height:1.5;">Materialidad: <b>Alta</b> si cumple ≥2 de 4 criterios, <b>Media</b> si 1, <b>Baja</b> si ninguno. Criterios (umbrales internos EMRISK, configurables): |Δ|≥${M.parametros?.materialidad?.monto_mdp} MDP, ≥${M.parametros?.materialidad?.pct_saldo_ant}% del saldo del mes anterior, ≥${M.parametros?.materialidad?.pct_captacion_inst}% de la captación de la institución del mes anterior, ≥${M.parametros?.materialidad?.pct_plaza_prod}% del mercado estado-producto. Clasificación combinada: Crítica+Alta→Prioridad crítica; Crítica+Media→Revisión prioritaria; Crítica+Baja→Atípica no material; Moderada+Alta→Material; Moderada+Media→Revisión; Moderada+Baja→Monitoreo. La recurrencia puede elevar la prioridad, nunca reducirla. Seguimiento sobre ${M.parametros?.K_seguimiento} meses.</div>
        ${resTxt}
      </div>` : ""}`;
      })()}
    </section>`;

  // ── 8. PRONÓSTICO ───────────────────────────────────────────────────
  const fc = s.forecast;

  // Preparar datos de la gráfica (misma lógica que ForecastChart React)
  const fcHistJSON = JSON.stringify(fc?.historico || []);
  const fcProyJSON = JSON.stringify(fc?.proyecciones || []);

  const secForecast = !fc ? "" :
    `<section style="margin-bottom:32px;">
      ${secHeader("📈","Pronóstico ARIMA")}
      ${insightBox(fc.insight)}
      ${fc.proyecciones?.length ? `
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;padding:16px 20px;margin-bottom:14px;">
        <div style="font-size:10px;font-weight:700;color:${C.text};font-family:monospace;letter-spacing:0.05em;margin-bottom:12px;">PROYECCIÓN AGREGADA — SERIES SELECCIONADAS${fc.cobertura_pct!=null?` (~${(fc.cobertura_pct).toFixed(1)}% de la captación ${fc.cobertura_scope&&fc.cobertura_scope!=="sistema"?"de "+esc(fc.cobertura_scope):"del sistema"})`:""} · mmdp</div>
        <div style="position:relative;height:260px;">
          <canvas id="forecast-chart"></canvas>
        </div>
      </div>` : ""}
      ${fc.proyecciones?.length ? `
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-top:14px;">
        <div style="padding:10px 16px;background:${C.surface};border-bottom:1px solid ${C.border};">
          <span style="font-size:10px;font-weight:700;font-family:monospace;color:${C.text};letter-spacing:0.05em;">VALORES PROYECTADOS</span>
        </div>
        ${(fc.cobertura_pct != null || fc.horizonte_nota) ? `
        <div style="padding:10px 16px;font-size:10px;color:${C.muted};line-height:1.6;border-bottom:1px solid ${C.border};">
          ${fc.cobertura_pct != null ? `Cobertura: las series proyectadas representan ~${(fc.cobertura_pct).toFixed(1)}% de la captación tradicional ${fc.cobertura_scope&&fc.cobertura_scope!=="sistema"?"de "+esc(fc.cobertura_scope):"del sistema"}${fc.cobertura_productos_pct!=null?` y ~${(fc.cobertura_productos_pct).toFixed(1)}% del saldo conjunto de Nivel 4 y Depósitos a Plazo`:""}${fc.confianza_pct!=null?` · intervalos al ${fc.confianza_pct}% de confianza`:""}.` : ""}
          ${fc.horizonte_nota ? `<div style="color:${C.amber};margin-top:3px;">⚠ ${esc(fc.horizonte_nota)}</div>` : ""}
        </div>` : ""}
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:${C.surface};">
            ${["Periodo","Entidad","Producto","Proyectado (mmdp)","Banda inf.","Banda sup.","Incert. rel.","Tipo"].map(h=>`<th style="${thS}">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${fc.proyecciones.map((p,i) => {
              const proy = p.saldo_proyectado_mdp;
              const rel  = (proy && p.banda_superior_mdp!=null && p.banda_inferior_mdp!=null) ? (p.banda_superior_mdp-p.banda_inferior_mdp)/proy*100 : null;
              const dentro = p.tipo === "dentro_muestra";
              return `
            <tr style="border-bottom:1px solid ${C.border}22;background:${dentro?C.amber+"14":(i%2===0?"transparent":C.surface+"80")};">
              <td style="${tdS}color:${C.muted};">${esc(String(p.periodo||"—"))}</td>
              <td style="${tdS}color:${C.text};">${esc(p.nombre_entidad||"—")}</td>
              <td style="${tdS}color:${C.text};">${esc(p.descripcion_producto||p.producto||"—")}</td>
              <td style="${tdS}color:${C.gold};font-weight:700;">${proy!=null?fB(proy/1000):"—"}</td>
              <td style="${tdS}color:${C.muted};">${p.banda_inferior_mdp!=null?fB(p.banda_inferior_mdp/1000):"—"}</td>
              <td style="${tdS}color:${C.muted};">${p.banda_superior_mdp!=null?fB(p.banda_superior_mdp/1000):"—"}</td>
              <td style="${tdS}color:${C.muted};">${rel!=null?rel.toFixed(1)+"%":"—"}</td>
              <td style="${tdS}color:${dentro?C.amber:C.muted};font-size:9px;">${dentro?"dentro de muestra":"pronóstico"}</td>
            </tr>`;}).join("")}
          </tbody>
        </table>
        <div style="padding:8px 16px;font-size:9px;color:${C.muted};line-height:1.5;">Nota: la banda del agregado en la gráfica es la suma de las bandas por serie; no es un intervalo estadístico válido para el total (no considera dependencia entre series).</div>
      </div>` : `<div style="color:${C.muted};font-size:12px;margin-top:14px;">Proyecciones no disponibles para esta entidad.</div>`}
    </section>`;

  // ── SCRIPT DEL MAPA ─────────────────────────────────────────────────
  const mapScript = `
var _terrData=${terrJSON};
var _isoMap=${isoMapJSON};
var _metrics=${terrMetricsJSON};
var _currentMetric='saldo_total_b';
var _geoLayer=null;
var _terrIdx={};
_terrData.forEach(function(d){if(d.cve_estado)_terrIdx[d.cve_estado]=d;});

function _sortedVals(metric){
  return _terrData.map(function(d){return d[metric]||0;}).filter(function(v){return v>0;}).sort(function(a,b){return a-b;});
}
function _cs(v,sv){
  if(!v||v<=0||!sv.length)return'#1a2535';
  var lo=0,hi=sv.length-1;
  while(lo<hi){var m=(lo+hi)>>1;if(sv[m]<v)lo=m+1;else hi=m;}
  var p=sv.length>1?lo/(sv.length-1):1;
  return p<0.08?'#1e3a8a':p<0.20?'#0369a1':p<0.34?'#0891b2':p<0.50?'#0d9488':p<0.65?'#16a34a':p<0.80?'#ca8a04':p<0.92?'#ea580c':'#dc2626';
}
function _rankColor(i,total){
  var p=total>1?(total-1-i)/(total-1):1;
  return p<0.08?'#1e3a8a':p<0.20?'#0369a1':p<0.34?'#0891b2':p<0.50?'#0d9488':p<0.65?'#16a34a':p<0.80?'#ca8a04':p<0.92?'#ea580c':'#dc2626';
}
function _fmtVal(val,fmt){
  if(fmt==='b') return (val||0).toFixed(2)+' mmdp';
  if(fmt==='mxn') return '$'+(val||0).toLocaleString('es-MX');
  return (val||0).toLocaleString('es-MX');
}
function _tipHtml(d,name){
  return '<b>'+(d.nombre_estado||name)+'</b><br>'+_metrics.map(function(m){return m.label+': '+_fmtVal(d[m.key],m.fmt);}).join('<br>');
}
function _buildRanking(metric){
  var mDef=_metrics.find(function(m){return m.key===metric;})||_metrics[0];
  var sorted=[..._terrData].sort(function(a,b){return (b[metric]||0)-(a[metric]||0);});
  var maxV=sorted[0]&&sorted[0][metric]?sorted[0][metric]:1;
  var total=sorted.length;
  return sorted.map(function(e,i){
    var val=e[metric]||0,pct=maxV>0?(val/maxV)*100:0,color=_rankColor(i,total);
    return '<div style="display:flex;align-items:center;gap:8px;padding:5px 14px;border-bottom:1px solid #1a253522;background:'+(i<3?color+'10':'transparent')+';">'
      +'<span style="font-size:10px;font-family:monospace;width:20px;text-align:right;color:'+(i<3?color:'#556a7a')+';font-weight:'+(i<3?700:400)+';"> '+(i+1)+'</span>'
      +'<div style="width:160px;flex-shrink:0;overflow:hidden;"><div style="font-size:11px;font-family:monospace;color:'+(i<3?'#dde4ee':'#8aa4b8')+';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">'+(e.nombre_estado||'')+'</div></div>'
      +'<div style="flex:1;height:5px;background:#1a2535;border-radius:3px;overflow:hidden;"><div style="width:'+Math.max(1,pct)+'%;height:100%;background:'+color+';border-radius:3px;"></div></div>'
      +'<span style="font-size:10px;font-family:monospace;width:90px;text-align:right;color:'+color+';">'+_fmtVal(val,mDef.fmt)+'</span></div>';
  }).join('');
}

function switchTerrMetric(metric){
  _currentMetric=metric;
  var mDef=_metrics.find(function(m){return m.key===metric;})||_metrics[0];
  document.querySelectorAll('.terr-btn').forEach(function(btn){
    var active=btn.dataset.metric===metric;
    btn.style.background=active?'#c8a96e22':'transparent';
    btn.style.color=active?'#c8a96e':'#8aa4b8';
    btn.style.border='1px solid '+(active?'#8a6d3b':'#1a2535');
    btn.style.fontWeight=active?700:400;
  });
  var lbl=document.getElementById('terr-metric-label');
  if(lbl) lbl.textContent=mDef.label;
  var rlbl=document.getElementById('ranking-metric-label');
  if(rlbl) rlbl.textContent=mDef.label;
  var rl=document.getElementById('ranking-list');
  if(rl) rl.innerHTML=_buildRanking(metric);
  if(_geoLayer){
    var sv=_sortedVals(metric);
    _geoLayer.setStyle(function(f){
      var cve=_isoMap[f.properties.id]||0,d=cve>0?_terrIdx[cve]:null;
      if(!d||!d[metric])return{fillColor:'#252836',fillOpacity:0.35,color:'#2a2f40',weight:0.5};
      return{fillColor:_cs(d[metric],sv),fillOpacity:0.85,color:'#070c12',weight:0.8};
    });
    _geoLayer.eachLayer(function(layer){
      var f=layer.feature,cve=_isoMap[f.properties.id]||0,d=cve>0?_terrIdx[cve]:null;
      if(d&&layer.getTooltip) layer.setTooltipContent(_tipHtml(d,f.properties.name||''));
    });
  }
}

(function(){
  var rl=document.getElementById('ranking-list');
  if(rl) rl.innerHTML=_buildRanking('saldo_total_b');
  var map=L.map('mapa-estados',{zoomControl:true,attributionControl:false}).setView([23.5,-102],5);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',{maxZoom:10}).addTo(map);
  fetch('https://raw.githubusercontent.com/angelnmara/geojson/master/mexicoHigh.json')
    .then(function(r){return r.json();})
    .then(function(geo){
      var sv=_sortedVals('saldo_total_b');
      _geoLayer=L.geoJSON(geo,{
        style:function(f){
          var cve=_isoMap[f.properties.id]||0,d=cve>0?_terrIdx[cve]:null;
          if(!d||!d.saldo_total_b)return{fillColor:'#252836',fillOpacity:0.35,color:'#2a2f40',weight:0.5};
          return{fillColor:_cs(d.saldo_total_b,sv),fillOpacity:0.85,color:'#070c12',weight:0.8};
        },
        onEachFeature:function(f,layer){
          var cve=_isoMap[f.properties.id]||0,d=cve>0?_terrIdx[cve]:null;
          layer.bindTooltip(d?_tipHtml(d,f.properties.name||''):(f.properties.name||''),{sticky:true});
        }
      }).addTo(map);
    }).catch(function(){});
})();`;

  // ── DOCUMENTO COMPLETO ──────────────────────────────────────────────
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>Reporte Captación R2421 — ${perFmt} — ${esc(ent)}</title>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
  <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"><\/script>
  <style>
    *,*::before,*::after{box-sizing:border-box;}
    html,body{margin:0;padding:0;background:#070c12;color:#dde4ee;font-family:monospace;}
    body{padding:24px 32px;max-width:1200px;margin:0 auto;}
    @media print{body{padding:10px 20px;}#mapa-estados{height:300px!important;}}
    .leaflet-tooltip{background:#0d1520;border:1px solid #1a2535;color:#dde4ee;font-family:monospace;font-size:11px;}
  </style>
</head>
<body>
  <div style="padding:16px 20px;background:#0d1520;border:1px solid #1a2535;border-radius:10px;margin-bottom:28px;display:flex;align-items:center;gap:16px;">
    <div style="font-size:28px;color:#c8a96e;">◫</div>
    <div style="flex:1;">
      <div style="font-size:9px;color:#8aa4b8;font-family:monospace;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:3px;">EMRISK · Fuente CNBV R24 B-2421 · Captación Bancaria</div>
      <div style="font-size:18px;font-weight:800;color:#c8a96e;font-family:monospace;letter-spacing:0.05em;">${esc(ent)}</div>
      <div style="font-size:11px;color:#556a7a;font-family:monospace;margin-top:4px;">Periodo: ${perFmt} · Generado: ${new Date().toLocaleString("es-MX")}</div>
    </div>
  </div>
  ${secResumen}
  ${secContribucion}
  ${secVista}
  ${secPlazo}
  ${secTerritorio}
  ${secInclusion}
  ${secConcentracion}
  ${secAnomalias}
  ${secForecast}
  <footer style="margin-top:40px;padding-top:14px;border-top:1px solid #1a2535;font-family:monospace;text-align:center;">
    <div style="font-size:9px;color:#556a7a;margin-bottom:6px;">
      Periodo ${perFmt} · Generado el ${new Date().toLocaleDateString("es-MX", {year:"numeric",month:"long",day:"numeric"})}
    </div>
    <div style="font-size:9px;color:#3a4a5a;max-width:680px;margin:0 auto;line-height:1.6;padding:8px 12px;border:1px solid #1a2535;border-radius:4px;background:#0d1520;">
      <strong style="color:#556a7a;">Aviso:</strong>
      Este documento fue generado de forma automatizada a partir de información pública contenida en el Reporte R2421
      publicado por la Comisión Nacional Bancaria y de Valores (CNBV). No constituye un documento oficial de la CNBV
      ni representa la postura, criterio o acción supervisora de dicha institución. Los comentarios analíticos e
      interpretaciones incluidos son producto de procesamiento estadístico automatizado y deben ser validados por
      personal especializado antes de utilizarse como insumo para decisiones regulatorias o de supervisión.
    </div>
  </footer>
  <script>${mapScript}<\/script>
  <script>
(function(){
  var hist=${fcHistJSON};
  var proy=${fcProyJSON};
  var el=document.getElementById('forecast-chart');
  if(!el||!hist.length&&!proy.length)return;
  var aggH={},aggP={},aggBL={},aggBU={};
  hist.forEach(function(h){aggH[h.periodo]=(aggH[h.periodo]||0)+h.saldo_historico_mdp;});
  proy.forEach(function(p){
    aggP[p.periodo]=(aggP[p.periodo]||0)+p.saldo_proyectado_mdp;
    aggBL[p.periodo]=(aggBL[p.periodo]||0)+p.banda_inferior_mdp;
    aggBU[p.periodo]=(aggBU[p.periodo]||0)+p.banda_superior_mdp;
  });
  var all=[...new Set([...Object.keys(aggH),...Object.keys(aggP)])].sort();
  var labels=all.map(function(p){return p.slice(0,4)+'-'+p.slice(4);});
  var hData=all.map(function(p){return aggH[p]!=null?aggH[p]/1e3:null;});
  var pData=all.map(function(p){return aggP[p]!=null?aggP[p]/1e3:null;});
  var blData=all.map(function(p){return aggBL[p]!=null?aggBL[p]/1e3:null;});
  var buData=all.map(function(p){return aggBU[p]!=null?aggBU[p]/1e3:null;});
  new Chart(el,{type:'line',data:{labels:labels,datasets:[
    {label:'Histórico',data:hData,borderColor:'#60a5fa',backgroundColor:'transparent',borderWidth:2,pointRadius:2,tension:0.3},
    {label:'Proyección',data:pData,borderColor:'#34d399',backgroundColor:'rgba(34,197,94,0.1)',borderWidth:2,borderDash:[5,3],pointRadius:3,tension:0.3},
    {label:'Banda sup.',data:buData,borderColor:'rgba(34,197,94,0.3)',backgroundColor:'rgba(34,197,94,0.08)',borderWidth:1,pointRadius:0,fill:'+1',tension:0.3},
    {label:'Banda inf.',data:blData,borderColor:'rgba(34,197,94,0.3)',backgroundColor:'rgba(34,197,94,0.08)',borderWidth:1,pointRadius:0,fill:false,tension:0.3},
  ]},options:{responsive:true,maintainAspectRatio:false,
    plugins:{legend:{position:'top',labels:{font:{size:10,family:'monospace'},boxWidth:12,color:'#8aa4b8'}}},
    scales:{
      x:{ticks:{font:{size:9,family:'monospace'},maxRotation:45,color:'#8aa4b8'},grid:{color:'#1a2535'}},
      y:{ticks:{font:{size:9,family:'monospace'},color:'#8aa4b8'},grid:{color:'#1a2535'},
         title:{display:true,text:'mmdp',font:{size:9,family:'monospace'},color:'#8aa4b8'}}
    }
  }});
})();
  <\/script>
</body>
</html>`;
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════

const ReporteCaptacion = memo(function ReporteCaptacion({ user }) {
  const [modo,       setModo]       = useState("sectorial");
  const [institucion, setInstitucion] = useState("");
  const [periodo,    setPeriodo]    = useState(PERIODOS[0]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [reporte,    setReporte]    = useState(null);
  const [mapMetrica, setMapMetrica] = useState("saldo_total_b");
  const [mapNivel,   setMapNivel]   = useState("estado");   // "estado" | "municipio"
  const [exportando, setExportando] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState("resumen");

  const secciones = [
    { id: "resumen",        icon: "📋", label: "Resumen" },
    { id: "contribucion",   icon: "🏦", label: "Contribución" },
    { id: "vista",          icon: "💧", label: "Vista" },
    { id: "plazo",          icon: "📅", label: "Plazo" },
    { id: "territorio",     icon: "🗺️", label: "Territorio" },
    { id: "inclusion",      icon: "🤝", label: "Inclusión" },
    { id: "concentracion",  icon: "🔍", label: "Concentración" },
    { id: "anomalias",      icon: "🚨", label: "Anomalías" },
    { id: "forecast",       icon: "📈", label: "Pronóstico" },
  ];

  // ── Generar reporte ─────────────────────────────────────────────────
  const generarReporte = async () => {
    if (modo === "institucional" && !institucion) {
      setError("Selecciona una institución.");
      return;
    }
    setLoading(true);
    setError(null);
    setReporte(null);

    try {
      const body = {
        modo,
        nombre_entidad: modo === "institucional" ? institucion : undefined,
        periodo: parseInt(periodo),
        usuario: user?.email || "",
      };

      const res = await fetch(`${API_URL}/reporte/captacion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }

      const data = await res.json();
      setReporte(data);
      setSeccionActiva("resumen");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const s = reporte?.secciones;

  // ── Descarga HTML ───────────────────────────────────────────────────
  const exportarHTML = () => {
    if (!reporte) return;
    setExportando(true);
    try {
      const html = generarHTMLExport(reporte);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `reporte_captacion_${reporte.periodo}_${(reporte.nombre_entidad||"sectorial").replace(/\s+/g,"_")}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[EXPORT]", e);
    } finally {
      setExportando(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%",
                  background: COLOR.bg, fontFamily: "monospace", color: COLOR.text }}>

      {/* ── TOP BAR ───────────────────────────────────────────────── */}
      <div style={{
        background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}`,
        padding: "0 18px", display: "flex", alignItems: "center",
        gap: 12, flexShrink: 0, height: 44,
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: "0.05em",
                      color: COLOR.gold, fontFamily: "monospace" }}>
          ◫ CAPTACIÓN
        </div>
        <div style={{ width: 1, height: 16, background: COLOR.border }} />
        <div style={{ fontSize: 14, color: COLOR.muted, flex: 1, letterSpacing: "0.02em" }}>
          EMRISK · Fuente CNBV R24 B-2421 · Captación Bancaria
        </div>

        {/* Modo */}
        <div style={{ display: "flex", background: COLOR.card,
                      border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: 2, gap: 2 }}>
          {["sectorial","institucional"].map(m => (
            <button key={m}
              onClick={() => { setModo(m); setReporte(null); }}
              style={{
                padding: "3px 11px", borderRadius: 4, fontSize: 13,
                fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "monospace",
                letterSpacing: "0.04em",
                background: modo === m ? `${COLOR.gold}22` : "transparent",
                color: modo === m ? COLOR.gold : COLOR.muted,
              }}>
              {m === "sectorial" ? "◧ Sectorial" : "◨ Institucional"}
            </button>
          ))}
        </div>

        {/* Periodo */}
        <select value={periodo} onChange={e => setPeriodo(e.target.value)}
          style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`,
                   color: COLOR.text, borderRadius: 5, padding: "3px 8px",
                   fontSize: 13, fontFamily: "monospace" }}>
          {PERIODOS.map(p => (
            <option key={p} value={p}>{`${p.slice(0,4)}-${p.slice(4)}`}</option>
          ))}
        </select>

        {/* Institución (solo modo institucional) */}
        {modo === "institucional" && (
          <select value={institucion} onChange={e => setInstitucion(e.target.value)}
            style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`,
                     color: COLOR.text, borderRadius: 5, padding: "3px 8px",
                     fontSize: 13, fontFamily: "monospace", minWidth: 150 }}>
            <option value="">— Selecciona banco —</option>
            {INSTITUCIONES.map(i => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        )}

        <button onClick={generarReporte} disabled={loading}
          style={{
            background: loading ? COLOR.card : `${COLOR.gold}22`,
            color: loading ? COLOR.dim : COLOR.gold,
            border: `1px solid ${loading ? COLOR.border : COLOR.goldDim}`,
            borderRadius: 5, padding: "3px 12px", fontSize: 13,
            fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.04em",
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 5,
          }}>
          {loading ? "· Generando..." : "✦ Generar reporte"}
        </button>

        {/* Botón descarga HTML — visible solo cuando hay reporte */}
        {reporte && (
          <button onClick={exportarHTML} disabled={exportando}
            title="Descargar el informe completo como archivo HTML"
            style={{
              background: exportando ? COLOR.card : `${COLOR.blue}18`,
              color: exportando ? COLOR.dim : COLOR.blue,
              border: `1px solid ${exportando ? COLOR.border : COLOR.blue + "55"}`,
              borderRadius: 5, padding: "3px 10px", fontSize: 13,
              fontWeight: 600, fontFamily: "monospace", letterSpacing: "0.03em",
              cursor: exportando ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
            }}>
            {exportando ? "↓ Generando..." : "↓ HTML"}
          </button>
        )}
      </div>

      {/* ── ERROR ─────────────────────────────────────────────────── */}
      {error && (
        <div style={{ background: `${COLOR.red}18`, color: COLOR.red,
                      padding: "7px 18px", fontSize: 14, fontFamily: "monospace",
                      borderBottom: `1px solid ${COLOR.red}44` }}>
          ▲ {error}
        </div>
      )}

      {/* ── ESTADO VACÍO / CARGANDO ───────────────────────────────── */}
      {!reporte && (
        <div style={{ flex: 1, display: "flex", alignItems: "center",
                      justifyContent: "center", flexDirection: "column", gap: 10 }}>
          {loading ? (
            <>
              <div style={{ fontSize: 24, color: COLOR.gold, animation: "spin 1s linear infinite" }}>◫</div>
              <div style={{ fontSize: 15, color: COLOR.muted }}>Generando reporte · puede tardar ~40s</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 36, color: COLOR.goldDim }}>◫</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: COLOR.text,
                            fontFamily: "monospace", letterSpacing: "0.05em" }}>
                REPORTE DE CAPTACIÓN BANCARIA
              </div>
              <div style={{ fontSize: 14, color: COLOR.muted }}>
                Selecciona el modo y periodo, luego haz clic en "Generar reporte"
              </div>
            </>
          )}
        </div>
      )}

      {/* ── REPORTE ───────────────────────────────────────────────── */}
      {reporte && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>

          {/* Meta del reporte */}
          <div style={{ padding: "6px 18px", background: COLOR.surface,
                        borderBottom: `1px solid ${COLOR.border}`,
                        fontSize: 13, color: COLOR.muted, fontFamily: "monospace" }}>
            {reporte.nombre_entidad} · Periodo {reporte.periodo} · {reporte.tiempo_s}s
          </div>

          {/* ── TABS ──────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 2, padding: "6px 12px",
                        borderBottom: `1px solid ${COLOR.border}`,
                        background: COLOR.surface, flexWrap: "wrap" }}>
            {secciones.map(sec => {
              const active = seccionActiva === sec.id;
              const badge = sec.id === "anomalias" ? (s?.anomalias?.filas?.length || 0) : 0;
              return (
                <button key={sec.id} onClick={() => setSeccionActiva(sec.id)}
                  style={{
                    padding: "4px 10px", borderRadius: 4, fontSize: 13,
                    fontFamily: "monospace", cursor: "pointer",
                    border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                    background: active ? `${COLOR.gold}18` : "transparent",
                    color: active ? COLOR.gold : COLOR.muted,
                    fontWeight: active ? 700 : 400,
                  }}>
                  {sec.icon} {sec.label}
                  {badge > 0 && <span style={{
                    marginLeft: 4, background: `${COLOR.red}22`, color: COLOR.red,
                    padding: "0 4px", borderRadius: 8, fontSize: 12
                  }}>{badge}</span>}
                </button>
              );
            })}
          </div>

          {/* ── CONTENIDO DE LA SECCIÓN ACTIVA ────────────────────── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px", background: COLOR.bg }}>

            {/* 1. RESUMEN */}
            {seccionActiva === "resumen" && (
              <div>
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                  <KpiCard label="Captación tradicional"
                    value={fmtB(s?.resumen?.kpis?.total_b)} unit=""
                    delta={s?.resumen?.kpis?.delta_total_yoy} deltaLabel="YoY" />
                  <KpiCard label="Captación vista"
                    value={fmtB(s?.resumen?.kpis?.vista_b)} unit=""
                    delta={s?.resumen?.kpis?.delta_vista_yoy} deltaLabel="YoY" />
                  <KpiCard label="Captación plazo"
                    value={fmtB(s?.resumen?.kpis?.plazo_b)} unit=""
                    delta={s?.resumen?.kpis?.delta_plazo_yoy} deltaLabel="YoY" />
                  <KpiCard label="Total cuentas"
                    value={`${fmt(s?.resumen?.kpis?.num_cuentas_m, 1)} M`} unit=""
                    delta={s?.resumen?.kpis?.delta_cuentas_mom} deltaLabel="MoM" />
                </div>
                <div style={{ fontSize: 11, color: COLOR.muted, lineHeight: 1.5, marginBottom: 14 }}>
La <strong>Captación tradicional</strong> corresponde a los productos de depósito del R24 B-2421: depósitos de exigibilidad inmediata + a plazo + cuenta global (saldos al cierre). <strong>No incluye títulos de crédito emitidos</strong> (no se reportan en el R24 B-2421); para instituciones sin títulos emitidos coincide con la Captación Tradicional del balance CNBV. Excluye flujos del periodo (cuentas incorporadas, reactivadas y prescritas) y saldos a favor de tarjeta de crédito. A nivel de sistema es la captación tradicional en depósitos <strong>cubierta por el R24 B-2421</strong>; no equivale a la "Captación Total" del balance.
                </div>
                <InsightBox text={s?.resumen?.narrativa} />
                {s?.comparativo && (
                  <div style={{ marginTop: 14, background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ padding: "10px 16px", background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}` }}>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em" }}>
                        {`${reporte.nombre_entidad} VS SISTEMA`}
                      </span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: COLOR.surface }}>
                            {["Indicador", reporte.nombre_entidad, "Sistema", "Dif. (pp)"].map(h => (
                              <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: COLOR.muted, fontWeight: 600, borderBottom: `1px solid ${COLOR.border}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[["Var. mensual tradicional","delta_total_mom"],["Var. anual tradicional","delta_total_yoy"],["Var. anual vista","delta_vista_yoy"],["Var. anual plazo","delta_plazo_yoy"]].map(([lbl,k],i) => {
                            const vb = s.comparativo.banco?.[k], vs = s.comparativo.sistema?.[k];
                            const dif = (vb != null && vs != null) ? vb - vs : null;
                            return (
                              <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}22`, background: i % 2 === 0 ? "transparent" : `${COLOR.surface}80` }}>
                                <td style={{ padding: "7px 12px", color: COLOR.muted }}>{lbl}</td>
                                <td style={{ padding: "7px 12px", color: COLOR.gold, fontWeight: 700 }}>{vb != null ? `${fmt(vb,1)}%` : "—"}</td>
                                <td style={{ padding: "7px 12px", color: COLOR.muted }}>{vs != null ? `${fmt(vs,1)}%` : "—"}</td>
                                <td style={{ padding: "7px 12px", color: dif == null ? COLOR.muted : dif >= 0 ? COLOR.green : COLOR.red }}>{dif != null ? `${dif > 0 ? "+" : ""}${fmt(dif,1)} pp` : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding: "10px 16px", fontSize: 12, color: COLOR.muted, lineHeight: 1.6, borderTop: `1px solid ${COLOR.border}` }}>
                      Participación de {reporte.nombre_entidad} en el sistema — captación tradicional {fmt(s.comparativo.participacion?.captacion_total_pct,2)}%, vista {fmt(s.comparativo.participacion?.vista_pct,2)}%, plazo {fmt(s.comparativo.participacion?.plazo_pct,2)}%, cuentas {fmt(s.comparativo.participacion?.cuentas_pct,2)}%.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 1b. CONTRIBUCIÓN AL CAMBIO */}
            {seccionActiva === "contribucion" && (
              <div>
                {!s?.contribucion ? (
                  <div style={{ color: COLOR.muted, fontSize: 15 }}>No hay datos de contribución.</div>
                ) : (() => {
                  const c = s.contribucion;
                  const sis = c.sistema || {};
                  const insts = c.instituciones || [];
                  const maxC = Math.max(...insts.slice(0,10).map(i => Math.abs((c.delta_mom_cero ? i.delta_mom_mdp : i.contrib_mom_pct) || 0)), 1);
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <KpiCard label="Captación tradicional (sistema)" value={fmtB((sis.saldo_act_mdp||0)/1000)} unit="" />
                        <KpiCard label="Δ mensual sistema" value={`${fmtN(Math.round(sis.delta_mom_mdp||0))} MDP`} unit="" delta={sis.delta_mom_pct} deltaLabel="MoM" />
                        <KpiCard label="Δ anual sistema" value={`${fmtN(Math.round(sis.delta_yoy_mdp||0))} MDP`} unit="" delta={sis.delta_yoy_pct} deltaLabel="YoY" />
                      </div>

                      <InsightBox text={c.narrativa} />

                      {c.concentracion?.mom && !c.delta_mom_cero && (
                        <div style={{ fontSize: 12, color: COLOR.muted, lineHeight: 1.6 }}>
                          Concentración del movimiento: las 3 mayores en el mismo sentido explican{" "}
                          <strong style={{ color: COLOR.text }}>{fmt(c.concentracion.mom.top3_pct,1)}%</strong> y las 5 mayores{" "}
                          <strong style={{ color: COLOR.text }}>{fmt(c.concentracion.mom.top5_pct,1)}%</strong> de la {c.concentracion.mom.sentido} del sistema (magnitud positiva). Puede exceder 100% por movimientos compensatorios de otras instituciones.
                        </div>
                      )}
                      {c.delta_mom_cero && (
                        <div style={{ fontSize: 12, color: COLOR.amber, lineHeight: 1.6 }}>
                          El cambio mensual del sistema es prácticamente nulo; se muestran los deltas en MDP sin porcentajes de contribución.
                        </div>
                      )}

                      {c.banco?.encontrado && (
                        <div style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 8, padding: "14px 16px" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em", marginBottom: 8 }}>
                            {`POSICIÓN DE ${(reporte.nombre_entidad||"").toUpperCase()}`}
                          </div>
                          <div style={{ fontSize: 13, color: COLOR.muted, lineHeight: 1.7 }}>
                            Posición <strong style={{ color: COLOR.gold }}>{c.banco.posicion}</strong> de {c.banco.de_total} por contribución mensual · contribución <strong style={{ color: COLOR.text }}>{c.banco.contrib_mom_pct != null ? fmt(c.banco.contrib_mom_pct,1)+"%" : "—"}</strong> · participación {fmt(c.banco.participacion_pct,2)}% · rol: <strong style={{ color: COLOR.text }}>{c.banco.rol}</strong>.
                          </div>
                        </div>
                      )}

                      <div style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 8, overflow: "hidden" }}>
                        <div style={{ padding: "10px 16px", background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}` }}>
                          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em" }}>TOP 10 POR CONTRIBUCIÓN AL CAMBIO MENSUAL</span>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: COLOR.surface }}>
                                {["Institución","Saldo","Saldo ant.","Saldo YoY","Δ MoM (MDP)","Δ MoM %","Contrib. MoM","Δ YoY (MDP)","Δ YoY %","Contrib. YoY","Part. %"].map(h => (
                                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: COLOR.muted, fontWeight: 600, borderBottom: `1px solid ${COLOR.border}` }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {insts.slice(0,10).map((i,k) => {
                                const cm = i.contrib_mom_pct;
                                const barW = Math.min(100, Math.abs((c.delta_mom_cero ? i.delta_mom_mdp : cm) || 0) / maxC * 100);
                                const pos = (i.delta_mom_mdp||0) >= 0;
                                const posY = (i.delta_yoy_mdp||0) >= 0;
                                return (
                                  <tr key={k} style={{ borderBottom: `1px solid ${COLOR.border}22`, background: k%2===0 ? "transparent" : `${COLOR.surface}80` }}>
                                    <td style={{ padding: "7px 12px", color: COLOR.text }}>{i.nombre}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.muted }}>{fmtN(Math.round(i.s_act||0))}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.muted }}>{fmtN(Math.round(i.s_ant||0))}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.muted }}>{fmtN(Math.round(i.s_yoy||0))}</td>
                                    <td style={{ padding: "7px 12px", color: pos ? COLOR.green : COLOR.red }}>{`${(i.delta_mom_mdp||0)>0?"+":""}${fmtN(Math.round(i.delta_mom_mdp||0))}`}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.muted }}>{i.delta_mom_pct != null ? `${fmt(i.delta_mom_pct,1)}%` : "—"}</td>
                                    <td style={{ padding: "7px 12px" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ color: pos ? COLOR.green : COLOR.red, fontWeight: 700, minWidth: 54 }}>{cm != null ? `${cm>0?"+":""}${fmt(cm,1)}%` : "—"}</span>
                                        <div style={{ flex: 1, height: 6, background: `${COLOR.border}`, borderRadius: 3, overflow: "hidden", minWidth: 40 }}>
                                          <div style={{ width: `${barW}%`, height: "100%", background: pos ? COLOR.green : COLOR.red, borderRadius: 3 }} />
                                        </div>
                                      </div>
                                    </td>
                                    <td style={{ padding: "7px 12px", color: posY ? COLOR.green : COLOR.red }}>{`${(i.delta_yoy_mdp||0)>0?"+":""}${fmtN(Math.round(i.delta_yoy_mdp||0))}`}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.muted }}>{i.delta_yoy_pct != null ? `${fmt(i.delta_yoy_pct,1)}%` : "—"}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.muted }}>{i.contrib_yoy_pct != null ? `${fmt(i.contrib_yoy_pct,1)}%` : "—"}</td>
                                    <td style={{ padding: "7px 12px", color: COLOR.gold }}>{fmt(i.participacion_pct,2)}%</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* 2. VISTA */}
            {seccionActiva === "vista" && (
              <div>
                {!s?.vista ? (
                  <div style={{ color: COLOR.muted, fontSize: 15 }}>No hay datos de captación a la vista.</div>
                ) : (
                  <div>
                    {/* KPI rápido activas vs inactivas */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                      <KpiCard label="Vista activas"
                        value={fmtB(s.vista.saldo_activas_b)} unit="" />
                      <KpiCard label="Vista inactivas / Global"
                        value={fmtB(s.vista.saldo_inactivas_b)} unit="" />
                      <KpiCard label="Total vista"
                        value={fmtB(s.vista.saldo_total_b)} unit="" />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR.muted,
                                      marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
                          Por producto (cuentas activas)
                        </div>
                        {(s.vista.por_producto || []).map((p, i) => (
                          <BarRow key={i}
                            label={p.nombre.replace(/Cuentas Transaccionales de Bajo Riesgo /g, "BR ")}
                            value={p.saldo_b}
                            maxValue={s.vista.por_producto[0]?.saldo_b || 1}
                            color={[COLOR.gold, COLOR.blue, COLOR.purple, COLOR.green, COLOR.amber][i] || COLOR.muted}
                            rightLabel={`${fmtB(p.saldo_b)} (${p.pct}%)`} />
                        ))}
                        <div style={{ marginTop: 10, fontSize: 14, color: COLOR.gray }}>
                          Saldo promedio: <strong style={{ color: COLOR.text }}>
                            ${fmtN(s.vista.saldo_promedio_mxn)}
                          </strong>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR.muted,
                                      marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
                          Por tenedor (cuentas activas)
                        </div>
                        {(s.vista.por_tenedor || []).map((t, i) => (
                          <BarRow key={i}
                            label={t.nombre.replace("Persona ", "").replace(" nacional", "")}
                            value={t.saldo_b}
                            maxValue={s.vista.por_tenedor[0]?.saldo_b || 1}
                            color={[COLOR.purple, COLOR.blue, COLOR.gold][i] || COLOR.dim}
                            rightLabel={`${fmtB(t.saldo_b)}`} />
                        ))}
                      </div>
                    </div>
                    {s.vista.nota_base && (
                      <div style={{ fontSize: 11, color: COLOR.muted, marginBottom: 16, lineHeight: 1.5 }}>
                        {s.vista.nota_base}
                      </div>
                    )}

                    {/* Desglose de inactividad (cuentas No Activas + Sin movimiento + Cuenta Global) */}
                    {(s.vista.resumen_inactividad || []).length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR.muted,
                                      marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
                          Cuentas inactivas y Cuenta Global (Art. 61 LIC)
                        </div>
                        {s.vista.resumen_inactividad.map((g, i) => (
                          <BarRow key={i}
                            label={g.grupo}
                            value={g.saldo_b}
                            maxValue={s.vista.resumen_inactividad[0]?.saldo_b || 1}
                            color={[COLOR.dim, COLOR.muted, "#556a7a"][i] || COLOR.dim}
                            rightLabel={`${fmtB(g.saldo_b)} · ${fmtN(g.num_cuentas)} ctas`} />
                        ))}
                        <div style={{ marginTop: 6, fontSize: 13, color: COLOR.dim,
                                      fontFamily: "monospace", lineHeight: 1.5 }}>
                          No activas: saldo ≤ 1k MXN sin movimiento 3 meses ·
                          Sin movimiento: saldo &gt; 1k MXN sin movimiento 3 meses ·
                          Cuenta Global: inactivas ≥ 3 años (Art. 61 LIC)
                        </div>
                      </div>
                    )}

                    <InsightBox text={s?.vista?.insight} />
                  </div>
                )}
              </div>
            )}

            {/* 3. PLAZO */}
            {seccionActiva === "plazo" && (
              <div>
                {!s?.plazo ? (
                  <div style={{ color: COLOR.muted, fontSize: 15 }}>No hay datos de captación a plazo.</div>
                ) : (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR.muted,
                                      marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
                          Por moneda
                        </div>
                        {(s.plazo.por_moneda || []).map((m, i) => (
                          <BarRow key={i} label={m.nombre}
                            value={m.saldo_b}
                            maxValue={s.plazo.por_moneda[0]?.saldo_b || 1}
                            color={i === 0 ? COLOR.gold : COLOR.amber}
                            rightLabel={`${fmtB(m.saldo_b)} (${m.pct}%)`} />
                        ))}
                        <div style={{ marginTop: 10, fontSize: 14, color: COLOR.gray }}>
                          Saldo promedio: <strong style={{ color: COLOR.text }}>
                            ${fmtN(s.plazo.saldo_promedio_mxn)}
                          </strong>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR.muted,
                                      marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
                          Por tenedor
                        </div>
                        {(s.plazo.por_tenedor || []).map((t, i) => (
                          <BarRow key={i}
                            label={t.nombre.replace("Persona ", "").replace(" nacional", "")}
                            value={t.saldo_b}
                            maxValue={s.plazo.por_tenedor[0]?.saldo_b || 1}
                            color={[COLOR.gold, COLOR.amber, COLOR.dim][i] || COLOR.dim}
                            rightLabel={`${fmtB(t.saldo_b)}`} />
                        ))}
                      </div>
                    </div>
                    <InsightBox text={s?.plazo?.insight} />
                  </div>
                )}
              </div>
            )}

            {/* 4. TERRITORIO */}
            {seccionActiva === "territorio" && (
              <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {!s?.territorio ? (
                  <div style={{ color: COLOR.muted, fontSize: 15 }}>No hay datos territoriales.</div>
                ) : (() => {
                  // Datos activos según nivel seleccionado
                  const esMunicipio = mapNivel === "municipio";
                  const datosActivos = esMunicipio
                    ? (s.territorio_municipio || [])
                    : s.territorio;

                  // Para municipios mostramos top 50 en ranking (hay ~2400+)
                  const RANK_LIMIT   = esMunicipio ? 50 : datosActivos.length;
                  const sorted = [...datosActivos]
                    .sort((a, b) => (b[mapMetrica] || 0) - (a[mapMetrica] || 0));
                  const sortedRank = sorted.slice(0, RANK_LIMIT);
                  const maxV = sorted[0]?.[mapMetrica] || 1;

                  // Color cuantil idéntico al mapa: el #1 es rojo, el último es azul
                  const rankColor = (rankIdx) => {
                    // Para municipios el top-50 cae todo en cuantil rojo si usamos sorted.length
                    // Usamos sortedRank.length para distribuir el gradiente dentro de lo visible
                    const total = sortedRank.length;
                    const pct = total > 1 ? (total - 1 - rankIdx) / (total - 1) : 1;
                    if (pct < 0.08) return "#1e3a8a";
                    if (pct < 0.20) return "#0369a1";
                    if (pct < 0.34) return "#0891b2";
                    if (pct < 0.50) return "#0d9488";
                    if (pct < 0.65) return "#16a34a";
                    if (pct < 0.80) return "#ca8a04";
                    if (pct < 0.92) return "#ea580c";
                    return "#dc2626";
                  };

                  const valStr = (val) => mapMetrica.includes("_b") ? fmtB(val)
                    : mapMetrica === "num_cuentas" ? fmtN(val)
                    : `$${fmtN(val)}`;

                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

                      {/* ── BARRA SUPERIOR: nivel + métrica + leyenda ── */}
                      <div style={{
                        display: "flex", alignItems: "center", gap: 6,
                        flexWrap: "wrap",
                        padding: "8px 12px",
                        background: COLOR.surface,
                        border: `1px solid ${COLOR.border}`,
                        borderRadius: 8,
                      }}>
                        <span style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace",
                                       flexShrink: 0 }}>NIVEL</span>
                        {[
                          { key: "estado",    label: "Estado" },
                          { key: "municipio", label: "Municipio" },
                        ].map(opt => (
                          <button key={opt.key} onClick={() => setMapNivel(opt.key)}
                            style={{
                              padding: "4px 10px", borderRadius: 4, fontSize: 13,
                              fontFamily: "monospace", cursor: "pointer",
                              border: `1px solid ${mapNivel === opt.key ? COLOR.blue : COLOR.border}`,
                              background: mapNivel === opt.key ? `${COLOR.blue}22` : "transparent",
                              color: mapNivel === opt.key ? COLOR.blue : COLOR.muted,
                              fontWeight: mapNivel === opt.key ? 700 : 400,
                            }}>
                            {opt.label}
                          </button>
                        ))}
                        <div style={{ width: 1, height: 18, background: COLOR.border,
                                      margin: "0 4px", flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace",
                                       flexShrink: 0 }}>MÉTRICA</span>
                        {Object.entries(METRIC_LABELS).map(([key, label]) => (
                          <button key={key} onClick={() => setMapMetrica(key)}
                            style={{
                              padding: "4px 12px", borderRadius: 4, fontSize: 13,
                              fontFamily: "monospace", cursor: "pointer",
                              border: `1px solid ${mapMetrica === key ? COLOR.gold : COLOR.border}`,
                              background: mapMetrica === key ? `${COLOR.gold}22` : "transparent",
                              color: mapMetrica === key ? COLOR.gold : COLOR.muted,
                              fontWeight: mapMetrica === key ? 700 : 400,
                            }}>
                            {label}
                          </button>
                        ))}
                        <div style={{ marginLeft: "auto" }}>
                          <MapaLeyenda />
                        </div>
                      </div>

                      {/* ── GRID PRINCIPAL 50 / 50 ── */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

                        {/* COLUMNA IZQUIERDA — MAPA */}
                        <div style={{
                          borderRadius: 8, overflow: "hidden",
                          border: `1px solid ${COLOR.border}`,
                          background: "#0a1118",
                        }}>
                          <div style={{
                            padding: "8px 14px", height: 36,
                            background: COLOR.surface,
                            borderBottom: `1px solid ${COLOR.border}`,
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                          }}>
                            <span style={{ fontSize: 14, fontFamily: "monospace",
                                           color: COLOR.text, fontWeight: 600 }}>
                              {esMunicipio ? "Mapa Municipal" : "Mapa por Estado"} — {METRIC_LABELS[mapMetrica]}
                            </span>
                            {!esMunicipio && datosActivos[0]?._periodo && datosActivos[0]._periodo !== reporte.periodo && (
                              <span style={{ fontSize: 12, color: COLOR.gold, fontFamily: "monospace", marginLeft: 8 }}>
                                datos: {datosActivos[0]._periodo} (último disponible)
                              </span>
                            )}
                          </div>
                          <div style={{ height: 360 }}>
                            {esMunicipio
                              ? <MapaCaptacionMunicipio
                                  municipioData={s.territorio_municipio || []}
                                  metrica={mapMetrica} />
                              : <MapaCaptacion
                                  territorioData={s.territorio}
                                  metrica={mapMetrica} />
                            }
                          </div>
                        </div>

                        {/* COLUMNA DERECHA — RANKING */}
                        <div style={{
                          borderRadius: 8, overflow: "hidden",
                          border: `1px solid ${COLOR.border}`,
                          background: COLOR.card,
                          display: "flex", flexDirection: "column",
                        }}>
                          <div style={{
                            padding: "8px 14px",
                            background: COLOR.surface,
                            borderBottom: `1px solid ${COLOR.border}`,
                            fontSize: 14, fontFamily: "monospace",
                            color: COLOR.text, fontWeight: 600,
                          }}>
                            Ranking — {METRIC_LABELS[mapMetrica]}
                            {esMunicipio && <span style={{ fontSize: 12, color: COLOR.dim, marginLeft: 6 }}>(top {RANK_LIMIT})</span>}
                          </div>
                          <div style={{ flex: 1, overflowY: "auto" }}>
                            {sortedRank.map((e, i) => {
                              const val    = e[mapMetrica] || 0;
                              const barPct = maxV > 0 ? (val / maxV) * 100 : 0;
                              const color  = rankColor(i);
                              const nombre = esMunicipio ? e.nombre_municipio : e.nombre_estado;
                              const sub    = esMunicipio ? e.nombre_estado : null;
                              return (
                                <div key={i} style={{
                                  display: "flex", alignItems: "center", gap: 8,
                                  padding: "5px 14px",
                                  borderBottom: `1px solid ${COLOR.border}22`,
                                  background: i < 3 ? `${color}10` : "transparent",
                                }}>
                                  <span style={{
                                    fontSize: 13, fontFamily: "monospace", flexShrink: 0,
                                    width: 20, textAlign: "right",
                                    color: i < 3 ? color : COLOR.dim,
                                    fontWeight: i < 3 ? 700 : 400,
                                  }}>{i + 1}</span>
                                  <div style={{ width: 150, flexShrink: 0, overflow: "hidden" }}>
                                    <div style={{
                                      fontSize: 14, fontFamily: "monospace",
                                      color: i < 3 ? COLOR.text : COLOR.muted,
                                      fontWeight: i < 3 ? 600 : 400,
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                    }}>{nombre}</div>
                                    {sub && (
                                      <div style={{ fontSize: 11, fontFamily: "monospace",
                                                    color: COLOR.dim, marginTop: 1 }}>{sub}</div>
                                    )}
                                  </div>
                                  <div style={{ flex: 1, height: 6, background: COLOR.border,
                                                borderRadius: 4, overflow: "hidden", minWidth: 40 }}>
                                    <div style={{
                                      width: `${barPct}%`, height: "100%", borderRadius: 4,
                                      background: color,
                                    }} />
                                  </div>
                                  <span style={{
                                    fontSize: 13, fontFamily: "monospace", flexShrink: 0,
                                    width: 80, textAlign: "right",
                                    color: i < 5 ? color : COLOR.muted,
                                    fontWeight: i < 3 ? 700 : 400,
                                  }}>{valStr(val)}</span>
                                </div>
                              );
                            })}
                            {esMunicipio && sorted.length > RANK_LIMIT && (
                              <div style={{
                                padding: "8px 14px", textAlign: "center",
                                fontSize: 12, fontFamily: "monospace", color: COLOR.dim,
                                borderTop: `1px solid ${COLOR.border}`,
                              }}>
                                … y {sorted.length - RANK_LIMIT} municipios más
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* 5. INCLUSIÓN */}
            {seccionActiva === "inclusion" && (
              <div>
                {!s?.inclusion ? (
                  <div style={{ color: COLOR.muted, fontSize: 15 }}>No hay datos de inclusión financiera.</div>
                ) : (() => {
                  const niveles    = s.inclusion.por_nivel  || [];
                  const generos    = s.inclusion.por_genero || [];
                  const totalCuentas = niveles.reduce((a, n) => a + (n.num_cuentas || 0), 0);
                  const totalSaldo   = niveles.reduce((a, n) => a + (n.saldo_b || 0), 0);
                  const maxCtas      = Math.max(...niveles.map(n => n.num_cuentas || 0), 1);
                  const saldoProm    = totalCuentas > 0
                    ? Math.round((totalSaldo * 1e9) / totalCuentas) : 0;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <KpiCard label="Cuentas inclusión" value={fmtN(totalCuentas)} unit="" />
                        <KpiCard label="Saldo inclusión" value={fmtB(totalSaldo)} unit="" />
                        <KpiCard label="Saldo promedio" value={`$${fmtN(saldoProm)}`} unit="" />
                      </div>
                      <div style={{
                        background: COLOR.card, border: `1px solid ${COLOR.border}`,
                        borderRadius: 8, padding: "16px 20px",
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: COLOR.text,
                                      fontFamily: "monospace", letterSpacing: "0.05em",
                                      marginBottom: 14 }}>PRODUCTOS DE INCLUSIÓN FINANCIERA</div>
                        {s.inclusion.nota_universo_nivel && (
                          <div style={{ fontSize: 11, color: COLOR.muted, marginBottom: 12, lineHeight: 1.5 }}>
                            {s.inclusion.nota_universo_nivel}
                          </div>
                        )}
                        {niveles.map((n, i) => (
                          <div key={i} style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between",
                                          alignItems: "center", marginBottom: 4 }}>
                              <span style={{ fontSize: 13, fontFamily: "monospace",
                                             color: COLOR.muted, maxWidth: 380 }}>{n.producto || n.descripcion_producto}</span>
                              <span style={{ fontSize: 13, fontFamily: "monospace",
                                             color: COLOR.gold, fontWeight: 700 }}>{fmtN(n.num_cuentas)}</span>
                            </div>
                            <div style={{ height: 5, background: COLOR.border, borderRadius: 3, overflow: "hidden" }}>
                              <div style={{
                                width: `${Math.max(1, (n.num_cuentas / maxCtas) * 100)}%`,
                                height: "100%", borderRadius: 3, background: COLOR.gold,
                              }} />
                            </div>
                          </div>
                        ))}
                      </div>
                      {generos.length > 0 && (
                        <div style={{
                          background: COLOR.card, border: `1px solid ${COLOR.border}`,
                          borderRadius: 8, padding: "16px 20px",
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR.text,
                                        fontFamily: "monospace", letterSpacing: "0.05em",
                                        marginBottom: 14 }}>DISTRIBUCIÓN POR GÉNERO</div>
                          {s.inclusion.nota_universo_genero && (
                            <div style={{ fontSize: 11, color: COLOR.amber, marginBottom: 12, lineHeight: 1.5 }}>
                              {s.inclusion.nota_universo_genero}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            {generos.map((g, i) => (
                              <div key={i} style={{
                                flex: 1, minWidth: 160, background: COLOR.surface,
                                border: `1px solid ${COLOR.border}`, borderRadius: 6,
                                padding: "12px 16px",
                              }}>
                                <div style={{ fontSize: 18, marginBottom: 4 }}>
                                  {(g.genero || g.descripcion_genero) === "Mujer" ? "♀️" : "♂️"}
                                </div>
                                <div style={{ fontSize: 15, fontFamily: "monospace",
                                              color: COLOR.text, fontWeight: 700 }}>
                                  {g.genero || g.descripcion_genero}
                                </div>
                                <div style={{ fontSize: 14, fontFamily: "monospace",
                                              color: COLOR.muted, marginTop: 6 }}>
                                  {fmtN(g.num_cuentas)} cuentas
                                </div>
                                <div style={{ fontSize: 14, fontFamily: "monospace", color: COLOR.gold }}>
                                  {fmtB(g.saldo_b)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <InsightBox text={s?.inclusion?.insight} />
                    </div>
                  );
                })()}
              </div>
            )}

            {/* 6. CONCENTRACIÓN */}
            {seccionActiva === "concentracion" && (
              <div>
                {s?.concentracion_geografica && (
                  <div style={{ marginBottom: 14, background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ padding: "10px 16px", background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}` }}>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em" }}>
                        {`CONCENTRACIÓN GEOGRÁFICA INTERNA — ${reporte.nombre_entidad}`}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "14px 16px" }}>
                      <KpiCard label="HHI geográfico" value={fmt(s.concentracion_geografica.hhi, 0)} unit="" />
                      <KpiCard label={`Top estado (${s.concentracion_geografica.top_estado})`} value={`${fmt(s.concentracion_geografica.top_estado_pct,1)}%`} unit="" />
                      <KpiCard label="Top-3 estados" value={`${fmt(s.concentracion_geografica.top3_pct,1)}%`} unit="" />
                      <KpiCard label="Top-5 estados" value={`${fmt(s.concentracion_geografica.top5_pct,1)}%`} unit="" />
                    </div>
                    <div style={{ padding: "0 16px 12px", fontSize: 11, color: COLOR.muted, lineHeight: 1.5 }}>
                      Distribución de la captación de {reporte.nombre_entidad} entre estados (riesgo de concentración geográfica del propio banco). HHI geográfico calculado sobre las participaciones por estado.
                    </div>
                  </div>
                )}
                {!s?.concentracion?.filas?.length ? (
                  <div style={{ color: COLOR.muted, fontSize: 15, padding: "20px 0" }}>
                    No hay datos de concentración disponibles.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <InsightBox text={s.concentracion.insight} />
                    <div style={{
                      background: COLOR.card, border: `1px solid ${COLOR.border}`,
                      borderRadius: 8, overflow: "hidden",
                    }}>
                      <div style={{
                        padding: "10px 16px", background: COLOR.surface,
                        borderBottom: `1px solid ${COLOR.border}`,
                        display: "flex", alignItems: "center",
                      }}>
                        <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace",
                                       color: COLOR.text, letterSpacing: "0.05em" }}>
                          ÍNDICE HHI — CONCENTRACIÓN DE MERCADO
                        </span>
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse",
                                        fontFamily: "monospace", fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: COLOR.surface }}>
                              {["Estado","Producto","HHI","Δ interanual (pts)","Dirección","Mayor participación","Participación"].map(h => (
                                <th key={h} style={{
                                  padding: "8px 12px", textAlign: "left",
                                  color: COLOR.muted, fontWeight: 600,
                                  borderBottom: `1px solid ${COLOR.border}`,
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {s.concentracion.filas.map((f, i) => {
                              const hhi   = f.hhi_actual || f.hhi || 0;
                              const nivel = hhi > 2500 ? COLOR.red : hhi > 1500 ? COLOR.amber : COLOR.green;
                              const pct   = f.share_dominante_pct ?? f.participacion_dominante;
                              const dHhi  = f.delta_hhi;
                              const dCol  = dHhi == null ? COLOR.muted
                                          : dHhi >= 100 ? COLOR.red
                                          : dHhi <= -100 ? COLOR.green
                                          : COLOR.muted;
                              return (
                                <tr key={i} style={{
                                  borderBottom: `1px solid ${COLOR.border}22`,
                                  background: i % 2 === 0 ? "transparent" : `${COLOR.surface}80`,
                                }}>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                                    {f.descripcion_estado || f.nombre_estado || "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.text }}>
                                    {f.descripcion_producto || "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px" }}>
                                    <span style={{ color: nivel, fontWeight: 700 }}>{fmtN(hhi)}</span>
                                  </td>
                                  <td style={{ padding: "7px 12px", color: dCol, fontWeight: 600 }}>
                                    {dHhi != null ? `${dHhi > 0 ? "+" : ""}${fmtN(dHhi)} pts` : "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                                    {f.direccion || "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.gold }}>
                                    {f.banco_dominante || "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                                    {pct != null ? `${fmt(pct, 1)}%` : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 10, lineHeight: 1.5 }}>
                      Las categorías de concentración (competitivo, moderada, alta, cuasi-monopolio)
                      corresponden a criterios analíticos de EMRISK sobre el índice HHI y no constituyen
                      umbrales regulatorios ni criterios de supervisión establecidos por la CNBV.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 7. ANOMALÍAS */}
            {seccionActiva === "anomalias" && (
              <div>
                <InsightBox text={s?.anomalias?.insight} />
                {(s?.anomalias?.filas?.length) ? (
                  <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 10, lineHeight: 1.5 }}>
                    Metodología: el z-score se calcula sobre la variación mensual (Δ) del saldo de cada
                    serie entidad × estado × producto. La media y la desviación estándar se estiman sobre
                    todo el histórico previo al periodo analizado (mínimo 12 meses; se excluyen series con
                    desviación estándar cero o menos de 12 observaciones). Umbrales de severidad:
                    |z| ≥ 3.0 crítica, 2.0 ≤ |z| &lt; 3.0 moderada (se listan casos con |z| ≥ 2.0).
                    "Crítica" indica magnitud estadística, no irregularidad.
                  </div>
                ) : null}

                {/* Feature 2 — Materialidad económica + seguimiento */}
                {s?.anomalias?.materialidad && (() => {
                  const M = s.anomalias.materialidad;
                  const seg = M.seguimiento || {};
                  const mz = M.matriz || {};
                  const cmp = M.comparativo || {};
                  const clasColor = (c) => c === "Prioridad crítica" ? COLOR.red : c === "Material" ? COLOR.amber : c === "Revisión" ? COLOR.gold : COLOR.muted;
                  const chip = (lbl, val, col) => (
                    <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: "8px 12px", minWidth: 92 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: col || COLOR.text, fontFamily: "monospace" }}>{val}</div>
                      <div style={{ fontSize: 11, color: COLOR.muted }}>{lbl}</div>
                    </div>
                  );
                  return (
                    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em" }}>
                        MATERIALIDAD ECONÓMICA Y SEGUIMIENTO
                      </div>
                      <InsightBox text={M.narrativa} />
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {chip("Nuevas", seg.nuevas ?? 0)}
                        {chip("Recurrentes", seg.recurrentes ?? 0)}
                        {chip("Persistentes", seg.persistentes ?? 0, COLOR.amber)}
                        {chip("Resueltas", seg.resueltas ?? 0, COLOR.green)}
                        {chip("Sin info", seg.sin_info ?? 0)}
                        {cmp.n_anterior != null && chip("Mes previo", cmp.n_anterior)}
                      </div>

                      {/* Matriz severidad × materialidad */}
                      <div style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 8, overflow: "hidden" }}>
                        <div style={{ padding: "10px 16px", background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}` }}>
                          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em" }}>MATRIZ SEVERIDAD × MATERIALIDAD</span>
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 13 }}>
                          <thead><tr style={{ background: COLOR.surface }}>
                            {["", "Materialidad Alta", "Media", "Baja"].map(h => (<th key={h} style={{ padding: "8px 12px", textAlign: "left", color: COLOR.muted, fontWeight: 600, borderBottom: `1px solid ${COLOR.border}` }}>{h}</th>))}
                          </tr></thead>
                          <tbody>
                            {["Crítica","Moderada"].map(sev => (
                              <tr key={sev} style={{ borderBottom: `1px solid ${COLOR.border}22` }}>
                                <td style={{ padding: "7px 12px", color: COLOR.text, fontWeight: 700 }}>{`z ${sev}`}</td>
                                {["Alta","Media","Baja"].map(mm => {
                                  const v = mz[`${sev}|${mm}`] || 0;
                                  const hot = sev === "Crítica" && mm === "Alta";
                                  return <td key={mm} style={{ padding: "7px 12px", color: hot && v > 0 ? COLOR.red : v > 0 ? COLOR.text : COLOR.muted, fontWeight: hot ? 700 : 400 }}>{v}</td>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Tabla priorizada */}
                      {M.anomalias?.length > 0 && (
                        <div style={{ background: COLOR.card, border: `1px solid ${COLOR.border}`, borderRadius: 8, overflow: "hidden" }}>
                          <div style={{ padding: "10px 16px", background: COLOR.surface, borderBottom: `1px solid ${COLOR.border}` }}>
                            <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", color: COLOR.text, letterSpacing: "0.05em" }}>ANOMALÍAS PRIORIZADAS (materialidad, luego z)</span>
                          </div>
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 12 }}>
                              <thead><tr style={{ background: COLOR.surface }}>
                                {["Institución","Estado","Producto","Saldo","Saldo ant.","z","Sev.","Δ MDP","% saldo prev","% capt.","% plaza","Materialidad","Clasificación","Seguim.","Meses"].map(h => (<th key={h} style={{ padding: "7px 10px", textAlign: "left", color: COLOR.muted, fontWeight: 600, borderBottom: `1px solid ${COLOR.border}` }}>{h}</th>))}
                              </tr></thead>
                              <tbody>
                                {M.anomalias.slice(0,20).map((a,k) => (
                                  <tr key={k} style={{ borderBottom: `1px solid ${COLOR.border}22`, background: k%2===0 ? "transparent" : `${COLOR.surface}80` }}>
                                    <td style={{ padding: "6px 10px", color: COLOR.text }}>{a.nombre_entidad}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.descripcion_estado}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{(a.descripcion_producto||"").replace(/Cuentas Transaccionales de Bajo Riesgo /,"BR ")}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{fmtN(Math.round(a.saldo_mdp||0))}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.saldo_ant_mdp != null ? fmtN(Math.round(a.saldo_ant_mdp)) : "—"}</td>
                                    <td style={{ padding: "6px 10px", color: Math.abs(a.z_score||0)>=3 ? COLOR.red : COLOR.amber, fontWeight: 700 }}>{a.z_score != null ? fmt(a.z_score,1) : "—"}</td>
                                    <td style={{ padding: "6px 10px", color: a.severidad_estadistica==="Crítica" ? COLOR.red : COLOR.muted }}>{a.severidad_estadistica}</td>
                                    <td style={{ padding: "6px 10px", color: (a.delta_mdp||0)>=0 ? COLOR.green : COLOR.red }}>{`${(a.delta_mdp||0)>0?"+":""}${fmtN(Math.round(a.delta_mdp||0))}`}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.delta_pct_saldo_ant != null ? `${fmt(a.delta_pct_saldo_ant,1)}%` : "—"}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.delta_pct_captacion_inst != null ? `${fmt(a.delta_pct_captacion_inst,2)}%` : "—"}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.delta_pct_plaza_prod != null ? `${fmt(a.delta_pct_plaza_prod,1)}%` : "—"}</td>
                                    <td style={{ padding: "6px 10px", color: a.materialidad==="Alta" ? COLOR.amber : COLOR.muted }}>{a.materialidad}</td>
                                    <td style={{ padding: "6px 10px", color: clasColor(a.clasificacion_combinada), fontWeight: 600 }}>{a.clasificacion_combinada}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.estado_seguimiento}</td>
                                    <td style={{ padding: "6px 10px", color: COLOR.muted }}>{a.meses_consecutivos}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div style={{ padding: "8px 16px", fontSize: 10, color: COLOR.muted, lineHeight: 1.5 }}>
                            Materialidad: <strong>Alta</strong> si cumple ≥2 de 4 criterios, <strong>Media</strong> si cumple 1, <strong>Baja</strong> si ninguno. Criterios (umbrales internos EMRISK, configurables): |Δ|≥{M.parametros?.materialidad?.monto_mdp} MDP, ≥{M.parametros?.materialidad?.pct_saldo_ant}% del saldo del mes anterior, ≥{M.parametros?.materialidad?.pct_captacion_inst}% de la captación de la institución del mes anterior, ≥{M.parametros?.materialidad?.pct_plaza_prod}% del mercado estado-producto. Clasificación combinada (z crítico ≥3 / moderado 2–3 × materialidad): Crítica+Alta→Prioridad crítica; Crítica+Media→Revisión prioritaria; Crítica+Baja→Atípica no material; Moderada+Alta→Material; Moderada+Media→Revisión; Moderada+Baja→Monitoreo. La recurrencia puede elevar la prioridad, nunca reducirla. Seguimiento sobre ventana de {M.parametros?.K_seguimiento} meses.
                          </div>
                        </div>
                      )}

                      {(M.resueltas?.length > 0 || M.sin_info?.length > 0) && (
                        <div style={{ fontSize: 11, color: COLOR.muted, lineHeight: 1.6 }}>
                          {M.resueltas?.length > 0 && (
                            <div><strong style={{ color: COLOR.green }}>Resueltas ({M.resueltas.length}):</strong> {M.resueltas.slice(0,8).map(r => `${r.nombre_entidad}/${r.descripcion_estado}/${(r.descripcion_producto||"").replace(/Cuentas Transaccionales de Bajo Riesgo /,"BR ")}${r.z_prev!=null?` (z previo ${fmt(r.z_prev,1)} → actual ${r.z_actual!=null?fmt(r.z_actual,1):"—"}, por debajo del umbral)`:""}`).join(" · ")}{M.resueltas.length>8?" …":""}</div>
                          )}
                          {M.sin_info?.length > 0 && (
                            <div style={{ marginTop: 4 }}><strong>Sin información suficiente ({M.sin_info.length}):</strong> {M.sin_info.slice(0,8).map(r => `${r.nombre_entidad}/${r.descripcion_estado}/${(r.descripcion_producto||"").replace(/Cuentas Transaccionales de Bajo Riesgo /,"BR ")}`).join(" · ")}{M.sin_info.length>8?" …":""} — sin dato en el periodo actual; no clasificadas como resueltas.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {!(s?.anomalias?.filas?.length) ? (
                  <div style={{ color: COLOR.muted, fontSize: 15, marginTop: 14 }}>
                    No se detectaron anomalías estadísticas en el periodo.
                  </div>
                ) : (
                  <div style={{ marginTop: 14,
                                background: COLOR.card, border: `1px solid ${COLOR.border}`,
                                borderRadius: 8, overflow: "hidden" }}>
                    <div style={{
                      padding: "10px 16px", background: COLOR.surface,
                      borderBottom: `1px solid ${COLOR.border}`,
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace",
                                     color: COLOR.text, letterSpacing: "0.05em" }}>
                        ANOMALÍAS ESTADÍSTICAS (Z-SCORE)
                      </span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse",
                                      fontFamily: "monospace", fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: COLOR.surface }}>
                            {["Entidad","Estado","Producto","Periodo","Z-score","Saldo (mmdp)"].map(h => (
                              <th key={h} style={{
                                padding: "8px 12px", textAlign: "left",
                                color: COLOR.muted, fontWeight: 600,
                                borderBottom: `1px solid ${COLOR.border}`,
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.anomalias.filas.map((f, i) => {
                            const z = Math.abs(f.z_score || 0);
                            const zColor = z > 3 ? COLOR.red : z > 2 ? COLOR.amber : COLOR.muted;
                            return (
                              <tr key={i} style={{
                                borderBottom: `1px solid ${COLOR.border}22`,
                                background: i % 2 === 0 ? "transparent" : `${COLOR.surface}80`,
                              }}>
                                <td style={{ padding: "7px 12px", color: COLOR.text }}>{f.nombre_entidad || "—"}</td>
                                <td style={{ padding: "7px 12px", color: COLOR.muted }}>{f.descripcion_estado || "—"}</td>
                                <td style={{ padding: "7px 12px", color: COLOR.muted }}>{f.descripcion_producto || "—"}</td>
                                <td style={{ padding: "7px 12px", color: COLOR.muted }}>{f.periodo || "—"}</td>
                                <td style={{ padding: "7px 12px" }}>
                                  <span style={{ color: zColor, fontWeight: 700 }}>
                                    {f.z_score != null ? fmt(f.z_score, 2) : "—"}
                                  </span>
                                </td>
                                <td style={{ padding: "7px 12px", color: COLOR.gold }}>
                                  {f.saldo_mdp != null ? fmtB(f.saldo_mdp / 1e3) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 8. PRONÓSTICO */}
            {seccionActiva === "forecast" && (
              <div>
                <InsightBox text={s?.forecast?.insight} />
                {!s?.forecast?.cubierto ? (
                  <div style={{ marginTop: 14, color: COLOR.muted, fontSize: 15 }}>
                    Pronóstico no disponible para esta entidad.
                    <div style={{ marginTop: 6, fontSize: 13, color: COLOR.dim }}>
                      Entidades con modelo ARIMA: {s.forecast.entidades_modelo?.join(", ")}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 14 }}>
                    <div style={{
                      background: COLOR.card, border: `1px solid ${COLOR.border}`,
                      borderRadius: 8, padding: "16px 20px",
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: COLOR.text,
                                    fontFamily: "monospace", letterSpacing: "0.05em",
                                    marginBottom: 12 }}>
                        {`PROYECCIÓN AGREGADA — SERIES SELECCIONADAS${s.forecast.cobertura_pct != null ? ` (~${(s.forecast.cobertura_pct).toFixed(1)}% de la captación ${s.forecast.cobertura_scope && s.forecast.cobertura_scope !== "sistema" ? "de " + s.forecast.cobertura_scope : "del sistema"})` : ""} · mmdp`}
                      </div>
                      <ForecastChart
                        historico={s.forecast.historico}
                        proyecciones={s.forecast.proyecciones} />
                      <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 8, lineHeight: 1.5 }}>
                        Nota: la banda del agregado es la suma de las bandas de cada serie individual;
                        no constituye un intervalo estadístico válido para el total, pues no considera
                        la dependencia entre series. Úsese como referencia visual, no como intervalo del agregado.
                      </div>
                    </div>
                    {(s.forecast.cobertura_pct != null || s.forecast.horizonte_nota) && (
                      <div style={{
                        marginTop: 12, background: COLOR.surface,
                        border: `1px solid ${COLOR.border}`, borderRadius: 8,
                        padding: "12px 16px", fontSize: 12, color: COLOR.muted, lineHeight: 1.6,
                      }}>
                        {s.forecast.cobertura_pct != null && (
                          <div>
                            Cobertura: las series proyectadas representan{" "}
                            <span style={{ color: COLOR.text, fontWeight: 700 }}>
                              ~{fmt(s.forecast.cobertura_pct, 1)}%
                            </span>{" "}
                            de la captación tradicional {s.forecast.cobertura_scope && s.forecast.cobertura_scope !== "sistema" ? `de ${s.forecast.cobertura_scope}` : "del sistema"}
                            {s.forecast.cobertura_productos_pct != null
                              ? ` y ~${fmt(s.forecast.cobertura_productos_pct, 1)}% del saldo conjunto de Nivel 4 y Depósitos a Plazo`
                              : ""}
                            {s.forecast.confianza_pct != null
                              ? ` · intervalos al ${s.forecast.confianza_pct}% de confianza`
                              : ""}.
                          </div>
                        )}
                        {s.forecast.horizonte_nota && (
                          <div style={{ marginTop: 4, color: COLOR.amber }}>
                            ⚠ {s.forecast.horizonte_nota}
                          </div>
                        )}
                      </div>
                    )}
                    {s.forecast.proyecciones?.length > 0 && (
                      <div style={{
                        marginTop: 12,
                        background: COLOR.card, border: `1px solid ${COLOR.border}`,
                        borderRadius: 8, overflow: "hidden",
                      }}>
                        <div style={{
                          padding: "10px 16px", background: COLOR.surface,
                          borderBottom: `1px solid ${COLOR.border}`,
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace",
                                         color: COLOR.text, letterSpacing: "0.05em" }}>
                            VALORES PROYECTADOS
                          </span>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse",
                                          fontFamily: "monospace", fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: COLOR.surface }}>
                                {["Periodo","Entidad","Producto","Proyectado (mmdp)","Banda inf.","Banda sup.","Incert. rel.","Tipo"].map(h => (
                                  <th key={h} style={{
                                    padding: "8px 12px", textAlign: "left",
                                    color: COLOR.muted, fontWeight: 600,
                                    borderBottom: `1px solid ${COLOR.border}`,
                                  }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {s.forecast.proyecciones.map((p, i) => {
                                const proy   = p.saldo_proyectado_mdp;
                                const rel    = (proy && p.banda_superior_mdp != null && p.banda_inferior_mdp != null)
                                  ? (p.banda_superior_mdp - p.banda_inferior_mdp) / proy * 100 : null;
                                const dentro = p.tipo === "dentro_muestra";
                                return (
                                <tr key={i} style={{
                                  borderBottom: `1px solid ${COLOR.border}22`,
                                  background: dentro ? `${COLOR.amber}14`
                                            : i % 2 === 0 ? "transparent" : `${COLOR.surface}80`,
                                }}>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>{p.periodo}</td>
                                  <td style={{ padding: "7px 12px", color: COLOR.text }}>{p.nombre_entidad || "—"}</td>
                                  <td style={{ padding: "7px 12px", color: COLOR.text }}>{p.descripcion_producto || p.producto}</td>
                                  <td style={{ padding: "7px 12px", color: COLOR.gold, fontWeight: 700 }}>
                                    {proy != null ? fmtB(proy / 1000) : "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                                    {p.banda_inferior_mdp != null ? fmtB(p.banda_inferior_mdp / 1000) : "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                                    {p.banda_superior_mdp != null ? fmtB(p.banda_superior_mdp / 1000) : "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                                    {rel != null ? `${fmt(rel, 1)}%` : "—"}
                                  </td>
                                  <td style={{ padding: "7px 12px", color: dentro ? COLOR.amber : COLOR.muted, fontSize: 11 }}>
                                    {dentro ? "dentro de muestra" : "pronóstico"}
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
});

export default ReporteCaptacion;
